/**
 * 모바일 관리자 — 접수(기본축).
 * 데스크탑 /adm/orders 와 같은 OrderListService 응답을 카드 리스트 + 상태 요약칩으로 렌더하고,
 * 카드 탭 시 오더 핸들 바텀시트(상태변경·수주·취소·금액·수수료·메모)를 연다.
 * 액션은 /ma/ajax/order/{n}/* → 데스크탑 정본(Admin\OrderController) 메서드에 그대로 위임한다.
 */
import { CAR_TYPE_BG, scrollSecTop } from './const';
import Util from '../../Common/Util';

const LENGTH = 20;

// 정본은 PHP `G5_ORDER_STATUS` — 서버가 /asset/bootstrap 으로 G5 에 심는다.
// 표에 없는 값은 생숫자를 찍지 않고 비운다.
const STATUS_LABEL = (typeof G5 !== 'undefined' && G5['ORDER_STATUS']) || {};

// 유형 문자열에서 아랫줄로 내리는 꼬리말 (좌측 패널 폭에 '지정수수료없음' 이 한 줄로 안 들어간다)
const NOFEE = '수수료없음';
// 유형 텍스트색 (/adm: 어플=red, 지정=#0080FF, 외부=blue)
const ORDER_TYPE_COLOR = [
    ['어플', '#E11D48'], ['외부', '#1D4ED8'], ['지정', '#0080FF'],
];

// 오더 상세 시트 탭 — PC 의 행확장(연락처·사진·로그)과 오더핸들(정보)을 한 시트에 나눠 담는다
const TABS = [
    { key: 'info',  label: '정보' },   // 오더정보 + 연락처 + 매칭
    { key: 'log',   label: '이력' },
    { key: 'photo', label: '사진' },
    { key: 'act',   label: '관리' },   // PC 오더핸들의 수정·상태전환
];

// 상세검색 카테고리별 opt 글자 (OrderListService serchtypes) — 다중선택
const ADV_MULTI = {
    type: ['A', 'B', 'C'],           // 배차유형: 일반·지정·외부
    pay:  ['D', 'E', 'F'],           // 결제: 직수·싸인·카드
    fee:  ['H', 'I', 'J', 'X', 'Y'], // 수수료: 0·7~10·15·16+·환원(Y=환원완료 OR 예외)
    time: ['L', 'M', 'N', 'O', 'P', 'Q'], // 작업시간: 종일·오전·오후·시간·야간·반납
};
// 수수료 그룹의 '환원'은 Y 다 (OrderListService::buildSearchSql 참조)
const ADV_ALL = [].concat(...Object.values(ADV_MULTI));
// 진행상태 칩(st) → opt 글자
const STATUS_LETTER = { '4': 'R', '6': 'S', '7': 'T', '8': 'U', '9': 'V', '99': 'W' };
// 상태축이 아닌 프리셋 칩 — 칩값 → OrderListService 특수조건(w).
// 지정발주(95)인데 지정된 기사가 아직 확인 안 한 건. /adm/orders 의 '지정미확인' 버튼과 같은 조건.
const PRESET_W = { w2: 2 };

// 작업내용 입력 한도 — 서버 정본은 OrderConfirmationService::WORK_CONTENT_MAX(_LINES).
// 넘겨도 서버가 자르지만, 잘린 뒤에 알면 늦어 화면에서 먼저 세어 보여준다.
const WORK_CONTENT_MAX = 66;
const WORK_CONTENT_MAX_LINES = 2;

// 편집 컬럼 → 상세 응답(info)의 키. 모달이 '지금 저장된 값'을 다시 읽을 때 쓴다.
const EDIT_KEY = {
    car_worktype: 'workType', contract_name: 'contractName',
    contract_message: 'memoBal', order_memo: 'memoAdm', bonsa_col: 'extraVal',
};

export default class Order {
    initialize() {
        this.$list    = $('#maOrderList');
        this.$more    = $('#maOrderMore');
        this.$moreBtn = $('#maOrderMoreBtn');
        this.$empty   = $('#maOrderEmpty');
        this.$loading = $('#maOrderLoading');
        this.$sum     = $('#maOrderSum');
        this.$chips   = $('#maOrderChips');

        this.admLevel = (typeof G5 !== 'undefined' && G5 && +G5.admLevel) || 0; // 관리자 등급(홀드 권한선)
        this.state = { st: 'all', start: 0, total: 0, loading: false };
        this.rows  = {}; // order_num → row (바텀시트 재료)

        // 상세검색 상태 — 다중선택은 Set(글자), 업종(c)·기준일(dt)은 단일값. 기본 전체 ON.
        // 소속(본사x/지사y/센터z)은 셀렉트 DOM 이 정본이라 여기 담지 않는다.
        this.adv = { sel: new Set(ADV_ALL), c: '', dt: 'n' };
        this.$adv       = $('#maOrderAdvanced');
        this.$advToggle = $('#maOrderAdvToggle');
        this.$advCount  = $('#maAdvCount');

        this.bind();
        this.bindDatePickers();
        this.bindSosok();
        this.openFromUrl();
        this.reload();
    }

    /**
     * ?open={오더번호} 로 들어오면 그 오더 상세를 바로 편다 — 대리진입에서 되돌아온 자리.
     *
     * **목록은 건드리지 않는다.** 검색칸에 번호를 넣으면 한 건만 남아 CS 가 보던 목록을 잃는다.
     * 시트 재료(카드 한 줄)만 따로 받아 온다 — 오더번호 검색은 서버에서 기간·상태 조건을
     * 통째로 대체하므로(OrderListService: `a.num in (...)`) 기본 기간 밖의 오더도 걸린다.
     *
     * 연 뒤에는 URL 에서 `open` 을 지운다 — 안 지우면 새로고침(F5)할 때마다 이 자리가
     * 재실행되어, 다른 오더를 보다 새로고침해도 계속 이 오더 상세가 다시 열렸다.
     */
    openFromUrl() {
        const n = parseInt(new URLSearchParams(location.search).get('open'), 10) || 0;
        if (!n) return;

        history.replaceState(null, '', '/ma/orders');

        $.getJSON('/ma/ajax/orders/list', { n: String(n), start: 0, length: 1 })
            .done((res) => {
                const row = ((res && res.data) || [])[0];
                if (!row) return;              // 지워졌거나 소속 밖 — 조용히 목록만 둔다
                this.rows[row.order_num] = row;
                this.openSheet(row);
            });
    }

    bind() {
        this.$chips.on('click', '.ma-chip', (e) => {
            const $c = $(e.currentTarget);
            this.$chips.find('.ma-chip').removeClass('is-active');
            $c.addClass('is-active');
            this.state.st = $c.data('st') + '';
            // 프리셋(지정미확인)은 상세검색을 전부 켜고 들어간다 — 서버가 w 조건을 다른 필터 위에
            // **얹기만** 하므로, 수수료 등이 꺼진 채로 들어가면 0건이 된다. (/adm OrderList.jijungmi)
            if (PRESET_W[this.state.st]) this.advSelectAll();
            this.reload();
        });

        $('#maOrderSearchBtn').on('click', () => this.reload());
        $('#maOrderKw').on('keydown', (e) => { if (e.key === 'Enter') this.reload(); });

        this.$moreBtn.on('click', () => this.fetchList());

        // 무한 스크롤 — '더보기' 자리가 화면에 들어오면 자동으로 다음 쪽을 붙인다.
        // attach 가 null 이면(IntersectionObserver 미지원) 위 버튼이 그대로 폴백으로 남는다.
        this._infinite = $j.Ui.InfiniteScroll.attach(this.$more[0], { onLoad: () => this.fetchList() });
        if (this._infinite) this.$more.addClass('is-auto');

        this.bindAdvanced();

        // 사진 등록 — $j.F.Upload 가 /api/upload 를 끝내면 여기로 돌아온다.
        // 같은 페이지의 다른 업로드와 섞이지 않게 context 를 반드시 확인한다.
        $j.Common.EventBus.on($j.getNS(this), 'upload:image', (e, payload) => {
            if ($j.F.Upload.getContext() !== 'maOrderPhoto') return;
            const files = Array.isArray(payload) ? payload : [payload];
            files.forEach((f) => f && f.id && this.savePhoto(f.id));
        });

        // 발주/수주자 클릭 → 그 회원으로 대리진입해 일지 (오더상세보다 먼저; 버블 차단)
        this.$list.on('click', '.ma-row-mem', (e) => {
            e.stopPropagation();
            const row = this.rows[$(e.currentTarget).closest('.ma-row').data('num')];
            this.proxyDiary($(e.currentTarget).data('owner'), row);
        });

        // 좌측 상세 버튼 · 카드 본문 어디를 눌러도 상세 시트 (PC 는 행 클릭 = 상세 펼침)
        this.$list.on('click', '.ma-row-detail', (e) => {
            const row = this.rows[$(e.currentTarget).data('num')];
            if (row) this.openSheet(row);
        });
        this.$list.on('click', '.ma-row-main', (e) => {
            const row = this.rows[$(e.currentTarget).closest('.ma-row').data('num')];
            if (row) this.openSheet(row);
        });
    }

    /**
     * 기간칸 — mobiscroll 달력. 앱 웹뷰의 안드로이드 기본 date 위젯 대신 쓴다.
     * 인풋은 readonly(키보드 차단)이므로 값 변경은 onChange 로만 들어온다.
     */
    bindDatePickers() {
        const $d = $('#maOrderD1, #maOrderD2');
        if (typeof $d.mobiscroll !== 'function') {   // vendor 미로드 시 최소 동작 보장
            $d.prop('readonly', false).on('change', () => this.reload());
            return;
        }
        $d.mobiscroll().datepicker({
            controls: ['calendar'],
            dateFormat: 'YYYY-MM-DD',
            locale: mobiscroll.localeKo,
            themeVariant: 'light',
            buttons: [],            // 날짜 탭 시 즉시 선택·닫힘
            onChange: () => this.reload(),
        });
    }

    /** 소속 3단 셀렉트 — $j.Common.Form 의 /ajax/sosok 헬퍼 (본사→지사→센터 연쇄) */
    bindSosok() {
        $j.Common.Form.updateBonsaSelectBox('', 'maAdvX', 'maAdvY', 'maAdvZ');
        $j.Common.Form.registerBonsaHandler('maAdvX', 'maAdvY', 'maAdvZ');
        $j.Common.Form.registerGisaHandler('maAdvY', 'maAdvZ');
        this.$adv.on('change', '#maAdvX, #maAdvY, #maAdvZ', () => this.updateAdvCount());
    }

    /** 선택된 소속코드 — 좁은 쪽(센터>지사>본사)이 이긴다. 서버도 같은 우선순위로 본다 */
    sosokVals() {
        return {
            x: ($('#maAdvX').val() || ''),
            y: ($('#maAdvY').val() || ''),
            z: ($('#maAdvZ').val() || ''),
        };
    }

    // ───────── 상세검색 ─────────

    bindAdvanced() {
        // 펼치기/접기
        this.$advToggle.on('click', () => {
            const open = this.$adv.prop('hidden');
            this.$adv.prop('hidden', !open);
            this.$advToggle.attr('aria-expanded', open ? 'true' : 'false')
                .toggleClass('is-open', open);
        });

        // 다중선택(카테고리) 토글
        this.$adv.on('click', '.ma-adv-opts[data-multi] .ma-adv-opt', (e) => {
            const $b = $(e.currentTarget);
            const letter = $b.data('opt') + '';
            if ($b.hasClass('is-on')) { $b.removeClass('is-on'); this.adv.sel.delete(letter); }
            else { $b.addClass('is-on'); this.adv.sel.add(letter); }
            this.updateAdvCount();
        });

        // 단일선택(업종 c / 기준일 dt) — 라디오
        this.$adv.on('click', '.ma-adv-opts[data-single] .ma-adv-opt', (e) => {
            const $b = $(e.currentTarget);
            const $grp = $b.closest('.ma-adv-opts');
            $grp.find('.ma-adv-opt').removeClass('is-on');
            $b.addClass('is-on');
            const key = $grp.data('single');
            this.adv[key] = ($b.data(key) != null ? $b.data(key) : '') + '';
            this.updateAdvCount();
        });

        // 발주자·수주자 — 자동완성(홀드 모달과 같은 정본)으로 '이름#번호' 를 채운다.
        // 엔터는 곧 적용이다 — 칸을 채우고 패널 아래 [적용]까지 내려가게 하지 않는다.
        this.$adv.find('[data-member]').each((_, el) => {
            this.bindMemberAc(el, document.getElementById(`${el.id}Ac`));
            el.addEventListener('input', () => this.updateAdvCount());
            el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { el.blur(); this.applyAdvanced(); } });
        });

        $('#maAdvApply').on('click', () => this.applyAdvanced());
        $('#maAdvReset').on('click', () => this.resetAdvanced());
    }

    applyAdvanced() {
        this.$adv.prop('hidden', true);
        this.$advToggle.removeClass('is-open').attr('aria-expanded', 'false');
        this.$adv.find('.ma-ac').prop('hidden', true).empty();
        this.reload();
    }

    /** 발주자(wb)·수주자(ws) 칸의 보이는 텍스트 그대로 */
    memberVals() {
        return {
            wb: ($('#maAdv_wb').val() || '').trim(),
            ws: ($('#maAdv_ws').val() || '').trim(),
        };
    }

    /**
     * 상세검색의 다중선택(배차유형·결제·수수료·작업시간)만 전부 켠다.
     * /adm 의 toggleAllOpt(true) 자리 — 업종·기준일·소속은 사용자가 고른 범위라 건드리지 않는다.
     * 목록 갱신은 부르는 쪽이 한다 (resetAdvanced 와 달리 여기서 reload 하지 않는다).
     */
    advSelectAll() {
        this.adv.sel = new Set(ADV_ALL);
        this.$adv.find('.ma-adv-opts[data-multi] .ma-adv-opt').addClass('is-on');
        this.updateAdvCount();
    }

    resetAdvanced() {
        this.adv = { sel: new Set(ADV_ALL), c: '', dt: 'n' };
        $('#maAdvX, #maAdvY, #maAdvZ').val('');
        $j.Common.Form.updateBonsaSelectBox('', 'maAdvX', 'maAdvY', 'maAdvZ');
        this.$adv.find('[data-member]').val('');
        this.$adv.find('.ma-ac').prop('hidden', true).empty();
        this.$adv.find('.ma-adv-opts[data-multi] .ma-adv-opt').addClass('is-on');
        this.$adv.find('.ma-adv-opts[data-single]').each((_, g) => {
            const $g = $(g);
            $g.find('.ma-adv-opt').removeClass('is-on').first().addClass('is-on');
        });
        this.updateAdvCount();
        this.reload();
    }

    /** 좁혀진 필터 개수(카테고리 일부해제 + 업종·소속·발주자·수주자 지정 + 작업일기준) → 토글 배지 */
    updateAdvCount() {
        let n = 0;
        Object.values(ADV_MULTI).forEach((letters) => {
            if (letters.some((l) => !this.adv.sel.has(l))) n++;
        });
        if (this.adv.c) n++;
        if (this.adv.dt !== 'n') n++;
        const so = this.sosokVals();
        if (so.x || so.y || so.z) n++;
        const mv = this.memberVals();
        if (mv.wb) n++;
        if (mv.ws) n++;
        this.$advCount.text(n || '').prop('hidden', n === 0);
    }

    /** opt = 선택된 비상태 글자 + 진행상태 칩(st) 글자 */
    buildOpt() {
        // 프리셋 칩(지정미확인)은 상태축이 아니라 별도 조건(w)이라 진행상태를 좁히지 않는다.
        // 서버가 w 조건에서 이미 order_status<6 으로 자른다.
        const status = (this.state.st === 'all' || !this.state.st || PRESET_W[this.state.st])
            ? 'RSTUVW'
            : (STATUS_LETTER[this.state.st] || 'RSTUVW');
        return [...this.adv.sel].join('') + status;
    }

    /**
     * 발주자·수주자 이름 → 그 회원으로 대리진입해 **그 오더의 일지 상세**를 연다.
     *
     * 목적지는 데스크탑과 같은 규약이다 — `/m/diary?showipicknum={오더번호}` 로 들어가면 일지가
     * 그 오더를 펼친 채 그려진다. (PC: OrderHandle.switchDiary · F/Admin/Util.userOrder)
     * 오더번호를 빼고 `/m/diary` 만 주면 달력만 뜨고 CS 가 그 오더를 다시 찾아야 한다.
     * 일지는 로그인한 회원 기준으로 그려지므로 **누구 이름을 눌렀는지**(발주자/수주자 소유자번호)가
     * 곧 누구의 일지인지를 정한다.
     *
     * 구: 기사상세 시트(기사관리 Member 재사용). 접수화면에서 이름을 누르는 것은 "이 사람이 이 일을
     * 어떻게 하고 있나"를 보려는 동작이라 회원정보 편집이 답이 아니었다. 기사상세·편집은 기사관리
     * (/ma/members)가 맡는다.
     *
     * 대리진입은 세션이 통째로 그 회원으로 바뀌므로 되돌릴 곳(from)을 함께 넘긴다 — 서버가 세션에
     * 적어두고 회원 화면의 복귀 알약($j.F.Mobile.ProxyBar)이 그걸로 돌아온다. (balEdit 과 같은 규약)
     *
     * @param {number} ownernum 회원(소유자)번호 — 누른 이름의 주인
     * @param {object} [row] 보던 오더. 없으면 일지 달력만 열고 복귀도 목록으로 간다
     */
    proxyDiary(ownernum, row) {
        const n = parseInt(ownernum, 10) || 0;
        if (!n) return this.toast('회원을 찾을 수 없습니다');

        const num  = row ? row.order_num : 0;
        const to   = num ? `/m/diary?showipicknum=${num}` : '/m/diary';
        const from = num ? `/ma/orders?open=${num}` : '/ma/orders';   // 복귀하면 보던 오더 상세로

        location.replace(`/ma/proxy/enter?mb_no=${n}`
            + `&returnurl=${encodeURIComponent(to)}`
            + `&from=${encodeURIComponent(from)}`);
    }

    /** 검색 파라미터 — 검색어가 숫자/콤마뿐이면 오더번호(n), 아니면 통합검색(stx) */
    params() {
        const kw = ($('#maOrderKw').val() || '').trim();
        const p = {
            d1: $('#maOrderD1').val(),
            d2: $('#maOrderD2').val(),
            st: this.state.st,
            opt: this.buildOpt(),   // 상세검색 + 진행상태를 합친 serchtypes
            dt: this.adv.dt,        // 기준일: n=접수일 / d=작업일
            length: LENGTH,
            start: this.state.start,
        };
        if (PRESET_W[this.state.st]) p.w = PRESET_W[this.state.st]; // 특수조건 (지정미확인)
        if (this.adv.c) p.c = this.adv.c; // 업종
        const so = this.sosokVals();      // 소속 — 본사/지사/센터
        if (so.x) p.x = so.x;
        if (so.y) p.y = so.y;
        if (so.z) p.z = so.z;
        const mv = this.memberVals();     // 발주자·수주자 — '이름#번호' 또는 이름
        if (mv.wb) p.wb = mv.wb;
        if (mv.ws) p.ws = mv.ws;
        if (kw) {
            if (/^[\d,\s]+$/.test(kw)) p.n = kw;
            else p.stx = kw;
        }
        return p;
    }

    reload() {
        this.state.start = 0;
        this.rows = {};
        this.$list.empty();
        this.$empty.prop('hidden', true);
        this.fetchList();
    }

    fetchList() {
        if (this.state.loading) return;
        this.state.loading = true;
        this.$loading.prop('hidden', false);
        this.$more.prop('hidden', true);

        $.getJSON('/ma/ajax/orders/list', this.params())
            .done((res) => {
                this.state.total = res.recordsTotal || 0;
                this.renderChips(res.statusCounts || {});
                this.renderSum(res.totalPrice || 0);

                const rows = res.data || [];
                rows.forEach((r) => {
                    this.rows[r.order_num] = r;
                    this.$list.append(this.cardHtml(r));
                });
                this.state.start += rows.length;

                this.$empty.prop('hidden', this.state.total !== 0);
                // rows 가 0건이면 total 이 뭐라 하든 멈춘다 — 무한 스크롤은 '더보기'와 달리 사람이
                // 멈춰주지 않아서, total 과 실제 행수가 어긋나면 같은 요청을 끝없이 반복한다.
                this.$more.prop('hidden', !(rows.length > 0 && this.state.start < this.state.total));

            })
            .fail((xhr) => this.toast(xhr.status === 401 ? '세션이 만료되었습니다' : '목록을 불러오지 못했습니다'))
            .always(() => {
                this.state.loading = false;
                this.$loading.prop('hidden', true);
            });
    }

    renderChips(counts) {
        this.$chips.find('.ma-chip').each((_, el) => {
            const st = $(el).data('st') + '';
            $(el).find('b').text(this.num(counts[st] || 0));
        });
    }

    /** 헤더 요약 — 건수·금액이 한눈에 들어와야 해서 숫자만 크게·색으로 뺀다 */
    renderSum(totalPrice) {
        this.$sum.html(
            `<b class="n">${this.num(this.state.total)}</b>건`
            + `<i class="sep">·</i>`
            + `<b class="w">${this.num(totalPrice)}</b>원`
        );
    }

    cardHtml(r) {
        const st = +r.order_status;
        const label = STATUS_LABEL[st] || '';
        const PAY = { 1: '싸인', 2: '직수', 5: '카드' };

        // 강조축: 유형 · 작업일시 · 수수료 · 작업메모 (adm/orders 강조와 동일)
        const spec = [r.car_range_str, r.option_str].filter(Boolean).join(' '); // 장비종류는 우측으로 뺌
        const fee  = +r.whan_status === 2 ? '환원' : (+r.whan_status === 1 ? '환원보류' : (r.fee_rate != null && r.fee_rate !== '' ? r.fee_rate + '%' : ''));
        const pay  = PAY[+r.pay_method] || '';
        const memo   = r.request_note || ''; // 작업(발주)메모
        const admemo = r.admin_memo || '';       // 관리자메모
        const sosok  = r.sosok_center || r.sosok_gisa || ''; // 발주자 소속(지사·센터)
        // 발주자 소속 본사가 장비불러가 아니면 좌측 색면 맨 위에 딱지를 세운다(partner_label).
        // 아래 줄의 sosok 은 지사·센터라 본사가 어디인지는 드러나지 않는다 — 그걸 이 딱지가 말한다.
        // 표기는 어플 알림과 동일 — 삼성은 '삼성에어컨', 그 외는 '협력사'.

        return `<div class="ma-row ma-row--order" data-num="${r.order_num}">
            <button type="button" class="ma-row-detail" data-num="${r.order_num}" style="background:${CAR_TYPE_BG[r.car_type] || '#FFFFFF'}" aria-label="오더 #${r.order_num} 상세">
                ${r.partner_label ? `<span class="ma-row-partner">${this.esc(r.partner_label)}</span>` : ''}
                <span class="ma-detail-num">#${r.order_num}</span>
                <span class="ma-row-cartype">${this.esc(r.car_type || '-')}</span>
                ${this.typeHtml(r.order_type_str)}
            </button>
            <div class="ma-row-main">
                <div class="ma-row-top">
                    <span class="ma-oc-status s-${st}">${this.esc(label)}</span>
                    ${r.hold_mb_no ? `<span class="ma-hold-tag">#${this.esc(r.hold_mb_no)} 홀드</span>` : ''}
                    <span class="ma-row-addr">${this.esc(r.site_address || '-')}</span>
                </div>
                <div class="ma-row-line">
                    ${this.whenHtml(r)}
                    ${spec ? `<span class="ma-row-spec">${this.esc(spec)}</span>` : ''}
                </div>
                <div class="ma-row-line ma-row-money">
                    <span class="ma-row-price">${this.num(r.car_price)}원</span>
                    ${fee ? `<span class="ma-row-fee">수수료 ${this.esc(fee)}</span>` : ''}
                    ${pay ? `<span class="ma-row-pay">${this.esc(pay)}</span>` : ''}
                </div>
                <div class="ma-row-line ma-row-parties">
                    ${sosok ? `<span class="ma-row-sosok">${this.esc(sosok)}</span>` : ''}
                    ${this.party(r.bal_name, r.order_owner_num)}
                    <i class="fas fa-arrow-right ma-row-arrow"></i>
                    ${this.party(r.driver_name, r.worker_owner_num)}
                    ${this.photoBadge(r)}
                </div>
                ${memo ? `<div class="ma-row-memo"><i class="far fa-comment-dots"></i>${this.esc(memo)}</div>` : ''}
                ${admemo ? `<div class="ma-row-admemo"><i class="fas fa-user-shield"></i>${this.esc(admemo)}</div>` : ''}
            </div>
        </div>`;
    }

    /**
     * 작업일시 — PC(/adm/orders) workTime 스타일 흉내:
     * 날짜(MM.DD) red·bold, 시간(HH:MM) navy·bold, 카키 배경, 자간 -1. '하루' 는 파랑.
     */
    whenHtml(r) {
        const wt = String(r.work_time || '');
        let d = '', t = '';
        if (wt.length >= 16) {                       // 'YYYY-MM-DD HH:MM…'
            d = wt.substring(5, 10).replace(/-/g, '.');
            t = wt.substring(11, 16);
        } else {
            d = (r.work_day || '').slice(5).replace('-', '.');
        }
        // 작업시간 표기(work_hour_str)와 작업유형(car_workdaytype)은 대부분 같은 말이다 —
        // '1일 일' · '3시간 시간' · '1톤짐 톤' · '야간2시간 야간' · '3개월 월'. 유형은 표기가
        // 담지 못한 것만 남긴다: 오전/오후(4시간 중 어느 반나절인지)가 사실상 전부다.
        // ('하루' 는 표기가 'N일' 로 나와 글자가 안 겹치므로 따로 걸러낸다)
        const hour  = r.work_hour_str ? this.esc(r.work_hour_str) : '';
        const dt    = String(r.work_day_type || '');
        const dtype = (!dt || dt === '하루' || hour.includes(dt)) ? '' : this.esc(dt);
        return `<span class="ma-when">${d ? `<b class="d">${this.esc(d)}</b>` : ''}${t ? `<b class="t">${this.esc(t)}</b>` : ''}</span>`
            + (hour ? `<span class="ma-when-hour">${hour}</span>` : '')
            + (dtype ? `<span class="ma-when-dtype">${dtype}</span>` : '');
    }

    /**
     * 유형 뱃지 — '지정수수료없음' 처럼 한 줄이 길어지는 값은 '수수료없음' 을 아랫줄로 내린다.
     * 아랫줄 색은 PC(/adm/orders)의 대괄호 텍스트색(#800000 갈색)과 같다.
     */
    typeHtml(str) {
        const s = String(str || '');
        if (!s) return '';
        const i = s.indexOf(NOFEE);
        const head = (i === -1) ? s : (s.slice(0, i) + s.slice(i + NOFEE.length));
        let html = '';
        if (head) html += `<span class="ma-row-ordertype" style="color:${this.orderTypeColor(head)}">${this.esc(head)}</span>`;
        if (i !== -1) html += `<span class="ma-row-ordertype-sub">${NOFEE}</span>`;
        return html;
    }

    /** 유형 문자열 → 텍스트색 (/adm/orders 규칙: 어플/외부/지정 부분일치) */
    orderTypeColor(str) {
        const s = String(str || '');
        const hit = ORDER_TYPE_COLOR.find(([kw]) => s.indexOf(kw) !== -1);
        return hit ? hit[1] : '#475569'; // 그 외(예약 등)는 슬레이트
    }

    /**
     * 작업사진 뱃지 — 사진이 있는 오더만 카메라 + 장수 (PC /adm/orders 발주자칸 카메라 아이콘과 같은 뜻).
     * g5_orders.photo_cnt = 작업사진(phototype 1,2) 개수.
     */
    photoBadge(r) {
        const n = parseInt(r.photo_cnt, 10) || 0;
        if (!n) return '';
        return `<span class="ma-row-photo" title="작업사진 ${n}장"><i class="fas fa-camera"></i>${n}</span>`;
    }

    /** 발주/수주자 — 번호 있으면 기사상세로 갈 수 있는 링크, 없으면 '-' */
    party(name, ownernum) {
        const n = parseInt(ownernum, 10) || 0;
        if (!n) return `<span class="ma-row-party none">${this.esc(name || '-')}</span>`;
        return `<button type="button" class="ma-row-party ma-row-mem" data-owner="${n}">${this.esc(name || '-')}<em>#${n}</em></button>`;
    }

    // ───────── 오더 상세 바텀시트 (PC 동선 재현: 행확장 + 오더핸들을 탭으로) ─────────
    //
    // PC(/adm/orders) 는 두 화면이다 — 행을 클릭하면 펼쳐지며 연락처·현장사진·로그가 나오고,
    // 오더번호(오더핸들)를 누르면 별도 창에서 상세정보와 수정 액션이 나온다.
    // 모바일은 창을 못 띄우므로 한 시트 안에서 탭으로 나누고, 수정 액션은 하단에 고정한다.

    openSheet(row) {
        const self = this;
        this.curRow = row;
        this.detail = null;      // 상세 응답 캐시 (탭 전환 시 재요청 안 함)
        this.tab    = 'info';
        this.editing  = false;   // 정보 탭 편집모드
        this.editBase = null;    // 편집 진입 시점의 값 (바뀐 칸만 저장하려고 쥔다)
        $j.Ui.BS.open({
            fixedHeight: true,   // 탭+고정 액션바가 있어 높이를 95vh 로 고정
            backClose: true,     // 안드로이드 뒤로가기 = 시트 닫기 (목록 스크롤·검색조건 보존)
            target: {
                init() {
                    self.curBs   = this.parent;
                    self.sheetEl = document.getElementById(this.id);
                    self.renderMain(self.sheetEl, row);
                    // 제목 옆 핸드폰 아이콘 = 발주자 계정으로 발주화면 열기(접수내용 수정·재발주).
                    // BS 헤더는 setTitle 이 html 을 그대로 받는다 — 버튼을 제목에 얹고 여기서 묶는다.
                    this.parent.setTitle(`오더 #${row.order_num}` + self.balIcon(row));
                    const bal = document.querySelector('.bottomsheet-panel > .header h2 .ma-hd-ic');
                    if (bal) bal.onclick = () => self.balEdit(row);
                },
                destroy() {},
            },
            data: { row },
        });
    }

    /** 상태별 가능한 상태변경 액션 (order_status + order_type 로 정밀 게이트) */
    statusActs(row) {
        const st = +row.order_status, type = +row.order_type;
        const a = [];
        if (type === 95 && st === 3) a.push('soojoo');   // 지정대기 대신수주 (acceptByAdmin 조건과 일치)
        if (st === 6) a.push('arrive');
        if (st === 6 || st === 7) a.push('done');
        if (st === 6 || st === 7 || st === 8) a.push('payDone');
        return a;
    }

    renderMain(el, row) {
        const st = +row.order_status;
        const label = STATUS_LABEL[st] || '';

        el.innerHTML = `
            <div class="ma-sheet">
                <div class="ma-sh-sum">
                    <div class="ma-sh-row1">
                        <span class="ma-oc-status s-${st}">${this.esc(label)}</span>
                        <span class="ma-sh-type">${this.esc(row.order_type_str || '')}</span>
                        <span class="ma-sh-price">${this.num(row.car_price)}원</span>
                    </div>
                    <div class="ma-sh-addr">${this.esc(row.site_address || '')}</div>
                    <div class="ma-sh-people">발주 ${this.esc(row.bal_name || '-')} → 수주 ${this.esc(row.driver_name || '-')}</div>
                </div>
                <div class="ma-tabs" role="tablist">
                    ${TABS.map((t) => `<button type="button" class="ma-tab${t.key === this.tab ? ' is-on' : ''}" data-tab="${t.key}" role="tab">${t.label}<b class="ma-tab-n" hidden></b></button>`).join('')}
                </div>
                <div class="ma-tabbody" id="maOhBody"></div>
            </div>`;

        el.querySelectorAll('.ma-tab').forEach((b) => {
            b.onclick = () => this.switchTab(b.dataset.tab);
        });
        this.renderTab();       // 상세 도착 전에도 카드가 쥔 값으로 정보탭을 먼저 그린다
        this.fetchDetail(row);
    }

    /** 관리 탭 — PC 오더핸들의 상태전환·수정 액션 (이 탭에서만 노출) */
    /**
     * 관리 탭 — PC 오더핸들 하단 툴바의 「관리」줄을 그대로 옮긴 자리.
     *
     * 버튼 구성·노출조건·비활성조건의 정본은 데스크탑 `Pages/Admin/OrderHandle.actionsBody()` 다.
     * 여기서 조건을 새로 만들지 않는다 — 두 화면이 다른 기준을 갖는 순간 CS 가 "폰에선 안 보인다"를
     * 오더 문제로 오해한다.
     *
     * ⚠ **취소된 오더(99)에서도 화면을 비우지 않는다.** 예전엔 '취소된 오더입니다' 한 줄만 두었는데,
     *   PC 는 그 상태에서도 페널티적용·환원원복·추가배차·외부배차전환(수주 없이 취소된 오더)을 낸다.
     *   실제로 CS 가 가장 자주 쓰는 자리라 폰에서만 없으면 매번 PC 를 열어야 했다.
     *   못 하는 것(오더재알림·금액·수수료·오더취소·외부배차전환)은 PC 와 같이 **흐리게 남겨** 둔다 —
     *   숨기면 "버튼이 사라졌다"가 되고, 남기면 "지금은 못 한다"가 된다.
     *
     * 상태·홀드는 카드값(row)만으로 그릴 수 있지만, 환원/페널티/회차비용은 상세(detail.act)가
     * 있어야 판정된다. 상세는 시트를 열 때 바로 받으므로 도착하면 renderTab 이 다시 그린다.
     */
    tabAct(row, d) {
        const st = +row.order_status;
        const a  = (d && d.act) || null;
        const off = st === 99 ? ' disabled' : '';   // PC 의 dCancel 과 같은 축
        // 페널티취소·적용·재알림·회차·외부배차전환은 서버 판정(OrderPolicyService::adminActBlocks → act.blocks)만 본다.
        // 상세가 오기 전(a 없음)엔 판정이 없으므로 그리지 않는다 — 재알림만 카드값(수주대기)으로 먼저 흐리게 둔다.
        const can = (k) => !!a && !!a.blocks && a.blocks[k] === '';
        const rePushOff = (a ? can('rePush') : st === 4) ? '' : ' disabled';

        const ACT_LABEL = { soojoo: '수주처리', arrive: '현장도착', done: '작업완료', payDone: '수금완료' };
        const statusBtns = this.statusActs(row)
            .map((x) => `<button type="button" class="ma-ab-btn primary" data-act="${x}">${ACT_LABEL[x]}</button>`)
            .join('');

        // 홀드 — 수주대기(4) + 어플오더(0) 에서만. 서버(OrderAcquisitionService::hold)의
        // UPDATE 조건이 `order_status=4 AND order_type=0` 이라 그 밖에선 눌러도 0행이다.
        // 권한선은 PC(/adm/orders 수주자칸 홀드 input)와 같은 level>=5.
        const canHold = st === 4 && +row.order_type === 0 && this.admLevel >= 5;
        const holdBtns = canHold
            ? `<button type="button" class="ma-ab-btn" data-act="hold">${row.hold_mb_no ? '홀드 변경' : '홀드 설정'}</button>`
              + (row.hold_mb_no ? '<button type="button" class="ma-ab-btn danger" data-act="unhold">홀드 해제</button>' : '')
            : '';

        const btn = (act, label, cls, dis) =>
            `<button type="button" class="ma-ab-btn${cls ? ' ' + cls : ''}" data-act="${act}"${dis || ''}>${label}</button>`;

        // 환원보류: 대상 판정은 서버 하나(OrderPolicyService::isWhanEligible → act.whanEligible)만 본다.
        const whanStatus = a ? +a.whanStatus : +row.whan_status;
        const whanDefer = (a && a.whanEligible && whanStatus !== 2)
            ? (whanStatus === 1 ? btn('whanRelease', '환원보류해제', 'warn') : btn('whanDefer', '환원보류', 'warn'))
            : '';

        // 환원처리: 수금완료(9)를 되돌려 환원시킨다 — 발주자가 준다고 해놓고 안 준 건.
        // 월배치는 order_status<9 만 훑어 이 건을 못 잡는다. 판정은 서버 하나
        // (OrderPolicyService::whanRevertBlockReason → act.whanRevertable)만 본다.
        const whanRevert = (a && a.whanRevertable) ? btn('whanRevert', '환원처리', 'danger') : '';

        // PC 「관리」줄과 같은 순서. 오더취소만 아래 위험구역으로 내린다(폰은 오조작이 잦다).
        const mngBtns = [
            btn('rePush', '오더재알림', '', rePushOff),
            btn('moreBae', '추가배차'),
            btn('price', '금액변경', '', off),
            btn('fee', '수수료변경', '', off),
            // 결제책임 — 허용조건은 서버가 상세에 싣는다(info.canPay = changeHowtopayByAdmin 과 같은 조건)
            btn('howtopay', '결제책임변경', '', (d && d.info && d.info.canPay) ? '' : ' disabled'),
            btn('outBae', '외부배차전환', '', can('outBae') ? '' : ' disabled'),
            can('outBaeEdit') ? btn('outBaeEdit', '외부배차정보변경') : '',
            (d && d.isInsurance) ? btn('mercury', '머큐리') : '',
            can('penaltyCancel') ? btn('penaltyCancel', '페널티취소', 'danger') : '',
            can('penaltyApply') ? btn('penaltyApply', '페널티적용', 'warn') : '',
            can('penaltyRevoke') ? btn('penaltyRevoke', '페널티해제', 'warn') : '',
            whanStatus === 2 ? btn('whanRollback', '환원원복', 'warn') : '',
            can('returnJob') ? btn('return', '회차(작업종료)', 'danger') : '',
            whanDefer,
            whanRevert,
            // 취소원복 — 드물게 쓰는 구제용이라 무채색으로 맨 끝 (PC 와 같은 자리)
            can('restore') ? btn('restore', '취소원복') : '',
        ].join('');

        return (statusBtns ? `<div class="ma-sh-sec"><h4>상태 변경</h4><div class="ma-act-btns">${statusBtns}</div></div>` : '')
            + (holdBtns
                ? `<div class="ma-sh-sec"><h4>수주대기 홀드</h4>`
                  + `<p class="ma-sh-desc">${row.hold_mb_no ? `지금은 <b>#${this.esc(row.hold_mb_no)}</b> 에게 홀드돼 있습니다.` : '특정 기사에게 이 오더를 잡아둡니다. 홀드된 기사만 수주할 수 있습니다.'}</p>`
                  + `<div class="ma-act-btns">${holdBtns}</div></div>`
                : '')
            // 탭 이름이 이미 '관리'라 소제목을 달지 않는다 (상태변경·홀드는 그 안의 갈래라 남긴다)
            + `<div class="ma-sh-sec"><div class="ma-act-btns">${mngBtns}</div></div>`
            + `<div class="ma-sh-sec"><div class="ma-act-btns">`
            + btn('cancel', '오더취소', 'danger wide', off)
            + `</div></div>`;
    }

    /**
     * 접수내용 수정 / 재발주 — 발주자로 대리로그인해 **회원 발주화면(/m/bal)** 을 연다.
     *
     * 관리자용 수정폼을 따로 두지 않는다. 그 화면이 관리자 쿠키(G5.isAdmin)를 보고 '오더수정'·
     * '오더수정+재발주' 버튼을 스스로 띄우기 때문이다 (F/Baljoo/Baljoo.js). 데스크탑 오더핸들도
     * 같은 주소를 아이프레임으로 띄운다 — 여기서는 폰이라 통째로 넘어갔다 복귀바로 돌아온다.
     *
     * 노출 조건은 그 화면의 버튼 조건과 맞춘다. 어긋나면 CS 가 저장 버튼이 없는 화면에 떨어진다.
     *   수정   order_type != 92(일지오더) && 상태 1~8
     *   재발주 위 조건 + 상태 < 5 + order_type == 0(어플발주)
     */
    balIcon(row) {
        const st = +row.order_status, type = +row.order_type;
        if (type === 92 || st < 1 || st > 8) return '';

        const canSend = st < 5 && type === 0;
        return '<button type="button" class="ma-hd-ic" aria-label="'
            + (canSend ? '접수내용 수정 · 재발주' : '접수내용 수정')
            + '"><i class="fas fa-mobile-screen"></i></button>';
    }

    /**
     * 진입 직전에 현재 상태를 다시 확인한다 — 카드가 그려진 뒤 다른 CS 가 취소·배차했을 수 있다.
     * 데스크탑이 같은 목적으로 쓰는 엔드포인트(Admin\OrderController::statusCheck)를 그대로 부른다.
     */
    balEdit(row) {
        const mbNo = +row.order_owner_num;
        if (!mbNo) return this.toast('발주자를 찾을 수 없습니다');

        $.getJSON(this.url(row, 'status-check')).done((info) => {
            if (info && info.canceled) return this.toast('이미 취소된 오더입니다');
            // 확인 모달은 뺐다 — 아이콘 하나를 누르는 동작이라 한 번 더 묻는 값이 없다.
            // 재발주 가능 여부는 넘어간 발주화면이 버튼으로 말하고, 되돌아오는 길은 관리자 복귀 알약이다.
            const re = `/m/bal?mode=myorderedit&editnum=${row.order_num}`;
            // 복귀하면 목록이 아니라 **보던 오더 상세**로 되돌아온다 (?open=)
            location.replace(`/ma/proxy/enter?mb_no=${mbNo}`
                + `&returnurl=${encodeURIComponent(re)}`
                + `&from=${encodeURIComponent(`/ma/orders?open=${row.order_num}`)}`);
        }).fail(() => this.toast('오더 상태를 확인하지 못했습니다'));
    }

    switchTab(key) {
        if (!key || key === this.tab) return;
        // 편집 중 탭을 옮기면 입력이 사라진다 — 고친 칸이 있으면 막고, 없으면 조용히 편집을 끝낸다
        if (this.editing) {
            if (this.dirtyEdits()) return this.toast('저장 또는 취소 후 이동해주세요');
            this.editing = false;
            this.editBase = null;
        }
        this.tab = key;
        const el = this.sheetEl;
        if (!el) return;
        el.querySelectorAll('.ma-tab').forEach((b) => b.classList.toggle('is-on', b.dataset.tab === key));
        this.renderTab();
        const body = el.querySelector('#maOhBody');
        if (body) body.scrollIntoView({ block: 'nearest' });
    }

    /** 현재 탭 본문 렌더 — 상세 응답(this.detail) 이 없으면 카드값으로 그릴 수 있는 만큼만 */
    renderTab() {
        const el = this.sheetEl;
        const box = el && el.querySelector('#maOhBody');
        if (!box) return;
        const row = this.curRow, d = this.detail;

        if (!d) {
            // 관리 탭은 카드값만으로도 절반은 그린다 (상태변경·홀드·재알림·금액·수수료·취소).
            // 환원·페널티·회차는 상세가 와야 판정되므로 도착 후 이 함수가 다시 그린다.
            if (this.tab === 'act') {
                box.innerHTML = this.tabAct(row, null);
                box.querySelectorAll('[data-act]').forEach((b) => { b.onclick = () => this.onAct(el, row, b.dataset.act); });
                return;
            }
            box.innerHTML = (this.tab === 'info')
                ? `<div class="ma-md-grid">${this.specRows(row)}</div><div class="ma-order-loading">상세 불러오는 중…</div>`
                : '<div class="ma-order-loading">상세 불러오는 중…</div>';
            return;
        }

        if (this.tab === 'info')  box.innerHTML = this.tabInfo(d, row);
        if (this.tab === 'photo') box.innerHTML = this.tabPhoto(d);
        if (this.tab === 'log')   box.innerHTML = this.tabLog(d);
        if (this.tab === 'act')   box.innerHTML = this.tabAct(row, d);
        // 저장바가 화면 맨 아래에 떠 있는 동안에는 본문 끝이 바에 가리지 않게 자리를 비운다
        box.classList.toggle('has-editbar', this.tab === 'info' && !!this.editing);

        // 관리 탭 액션 버튼
        box.querySelectorAll('[data-act]').forEach((b) => {
            b.onclick = () => this.onAct(el, row, b.dataset.act);
        });

        // 편집모드 — 연필(on) / 하단 저장바(off·save)
        box.querySelectorAll('[data-editmode]').forEach((b) => {
            const m = b.dataset.editmode;
            b.onclick = () => (m === 'save' ? this.saveEdits(row) : this.toggleEdit(m === 'on'));
        });
        if (this.editing) this.bindEditors(box);

        // 회원 링크(발주/수주/기사) → 그 회원으로 대리진입해 일지 (복귀하면 이 오더 상세로 되돌아온다)
        box.querySelectorAll('.ma-sh-mem').forEach((b) => {
            b.onclick = () => this.proxyDiary(b.dataset.owner, row);
        });
        // 사진 줄의 ＋ → 그 종류로 업로드
        box.querySelectorAll('.ma-photo-add').forEach((b) => {
            b.onclick = () => this.pickPhoto(b.dataset.kind, row);
        });
        // 사진 → 전체화면 갤러리
        box.querySelectorAll('.ma-photo').forEach((img) => {
            img.onclick = () => $j.Ui.ZoomPhoto.open(null, { items: this._gallery, index: +img.dataset.idx, downloadable: true });
        });
    }

    /** 정보 탭 — PC 오더핸들 '오더 정보' 카드 항목 순서 그대로 */
    tabInfo(d, row) {
        const i = d.info || {}, p = d.parties || {};
        const money = (v) => `${this.num(v)}원`;

        // 진행상태는 목록 카드와 같은 색 뱃지로 (PC 오더핸들 '오더 정보' 카드 첫 줄과 같은 자리).
        // 홀드는 수주대기의 하위 상태라 같은 줄에 붙인다 — 별도 줄로 빼면 눈에 안 띈다.
        const st = +row.order_status;
        const statusHtml = `<span class="ma-oc-status s-${st}">${this.esc(i.statusStr || STATUS_LABEL[st] || '')}</span>`
            + (row.hold_mb_no ? `<span class="ma-hold-tag">#${this.esc(row.hold_mb_no)} 홀드</span>` : '');

        const rows = [
            ['작업일시', this.dt(i.workTime)], ['소요시간', i.workHour],
            ['등록시간', this.dt(i.regDate)],  ['수주시간', this.dt(i.soojooDate) || '수주이전'],
            ['기종/옵션', i.carSpec + (i.isEmergency ? ' · 긴급' : '')],
            ['수수료율', i.feeStr], ['결제책임', i.howtopay],
            ['가격', money(i.price)], ['업체가격', i.priceBonsa ? money(i.priceBonsa) : ''],
            ['작업차량', i.carPlate],
            ['현장번호', i.fieldNumber], ['거래처번호', i.contractNumber],
        ];

        // 편집 가능한 칸 — 데스크탑 오더핸들의 인라인 편집셀(.editor)과 **같은 5개**다.
        // 서버 화이트리스트가 정본(OrderModificationService::EDITABLE_COLS) — 여기만 늘려도 저장 안 된다.
        const ed = [
            ['작업내용', 'car_worktype', i.workType, WORK_CONTENT_MAX],
            ['거래처명', 'contract_name', i.contractName, 0],
            ['발주메모', 'contract_message', i.memoBal, 0],
            ['관리메모', 'order_memo', i.memoAdm, 0],
        ];
        if (i.extraName) ed.push([i.extraName, 'bonsa_col', i.extraVal, 0]);

        const grid = this.kvRaw('진행상태', statusHtml)
            + (i.typeStr ? this.kv('배차유형', i.typeStr) : '')
            + rows.filter((r) => r[1] != null && r[1] !== '')
                .map((r) => this.kv(r[0], r[1])).join('');

        // 값이 비어도 항상 낸다 — 비면 안 보이던 예전 규칙이면 '없는 메모를 새로 넣을' 자리가 없다.
        const memo = ed.map((e) => this.editRow(e[0], e[1], e[2], e[3])).join('');

        // 편집 진입 버튼 — **고칠 칸 바로 위** 한 자리뿐이다. 취소된 오더는 고칠 것이 없다.
        // 섹션 제목(오더 정보) 옆에도 두던 것을 뺐다 — 읽기 그리드(작업일시·가격·번호…)가 길어
        // 제목 옆 버튼은 정작 고칠 칸이 보일 때쯤 화면 밖으로 밀려 있었다.
        const canEdit = +row.order_status !== 99;
        const editBtnHere = (canEdit && !this.editing)
            ? '<div class="ma-info-head"><button type="button" class="ma-sec-edit" data-editmode="on">'
              + '<i class="fas fa-pen"></i>수정</button></div>'
            : '';

        // 지도는 링크로 열지 않는다 — 앱 웹뷰에서 window.open/새창이 막혀 빈 창만 뜬다
        const addr = i.address
            ? `<div class="ma-sh-sec"><h4>현장</h4><div class="ma-sh-addrline">${this.esc(i.address)}</div></div>`
            : '';

        return `<div class="ma-sh-sec"><h4>연락처</h4>${this.partyRows(p)}</div>`
            + addr
            + '<div class="ma-sh-sec"><h4>오더 정보</h4>'
            + `<div class="ma-md-grid">${grid}</div>${editBtnHere}`
            + `<div data-sec="memo" class="ma-md-grid ma-ed-grid${this.editing ? ' is-editing' : ''}">${memo}</div></div>`
            + this.matchBlock(d)
            + this.editBar();
    }

    /** 편집 중에만 뜨는 하단 고정 저장바 (content-area 에 sticky) */
    editBar() {
        if (!this.editing) return '';
        return '<div class="ma-edit-bar is-float">'
            + '<button type="button" class="ma-ab-btn" data-editmode="off">취소</button>'
            + '<button type="button" class="ma-ab-btn primary" data-editmode="save">저장</button></div>';
    }

    /** 발주/수주/작업기사 연락처 줄 (이름 탭 → 기사상세, 번호 탭 → 전화걸기) */
    partyRows(p) {
        const line = (label, o, cls) => {
            if (!o || (!o.name && !o.phone)) return '';
            const name = o.num
                ? `<button type="button" class="ma-sh-mem" data-owner="${o.num}">${this.esc(o.name || '-')}<em>#${o.num}</em></button>`
                : `<span>${this.esc(o.name || '-')}</span>`;
            const tel = o.phone
                ? `<a class="ma-tel" href="tel:${this.esc(o.phone)}"><i class="fas fa-phone"></i> ${this.esc(o.phone)}</a>`
                : '<span class="ma-tel-none">번호없음</span>';
            return `<div class="ma-contact${cls ? ' ' + cls : ''}"><span class="ma-contact-k">${label}</span>${name}${tel}</div>`;
        };
        return line('발주', p.bal) + line('발주소속', p.balSub, 'is-sub')
             + line('수주', p.worker) + line('작업기사', p.driver, 'is-sub');
    }

    /** 매칭 블록(정보 탭 하단) — PC 행확장의 매칭정보 + 오더핸들 하단 매칭발송 바 */
    matchBlock(d) {
        const m = d.match || {};
        const pct = m.totalPush > 0 ? Math.round((m.sent / m.totalPush) * 100) : 0;

        const bar = m.totalPush > 0
            ? `<div class="ma-mt-bar"><div class="ma-mt-fill" style="width:${pct}%"></div></div>
               <div class="ma-mt-legend">
                   <span><i class="sent"></i>발송 ${this.num(m.sent)}명</span>
                   <span><i class="rest"></i>미발송 ${this.num(Math.max(0, m.totalPush - m.sent))}명</span>
                   <b>${pct}%</b>
               </div>`
            : '<div class="ma-sh-note">매칭 발송 내역이 없습니다</div>';

        const grid = [
            ['매칭유형', m.typeStr],
            ['오더구분', m.isToday ? '당일' : '당일외'],
            ['진행단계', m.togoAll ? `${m.togoNow} / ${m.togoAll} 차수` : ''],
            ['발송단계', m.stages ? `${m.sentStages} / ${m.stages} 단계` : ''],
            ['총 매칭', m.totalPush ? `${this.num(m.totalPush)}명` : ''],
            // 발주/수주시간은 위 '오더 정보'(등록시간·수주시간)와 같은 값이라 여기선 뺀다
        ].filter((r) => r[1] != null && r[1] !== '').map((r) => this.kv(r[0], r[1])).join('');

        return `<div class="ma-sh-sec"><h4>매칭</h4>${bar}<div class="ma-md-grid">${grid}</div></div>`;
    }

    /**
     * 사진 탭 — 데스크탑 오더핸들과 **같은 줄 구성**이다. 줄 = phototype 이고, 줄마다 ＋ 로 그
     * 종류의 사진을 CS 가 직접 올린다(외부배차는 올릴 수주자가 아예 없고, 일반 오더도 기사가 못
     * 올렸거나 잘못 올린 경우 CS 가 채워야 한다). 원본은 한 갤러리로 모아 전체화면 스와이프.
     */
    tabPhoto(d) {
        this._gallery = [];
        const P = d.photos || {};
        const names = d.photoKinds || {};

        // 전자작업확인서는 서명문서라 따로 세운다 — photoThumbs 의 doc 줄에도 아이콘(n=0)이 들어
        // 있지만 그건 원본이 없어 눌러도 열리지 않는다. 실물은 이 칸이 보여준다.
        const docSec = d.docPhoto
            ? `<div class="ma-sh-sec"><h4>서류사진</h4><div class="ma-photos">${this.photoImg(d.docPhoto)}</div></div>` : '';

        const rows = [
            ['작업문서', P.doc,  ['doc']],
            ['현장사진', P.work, ['before', 'during', 'after']],
        ];
        // 정산서류 줄은 보험정산 오더에만 상시. 일반 오더는 이미 등록된 게 있을 때만 (PC 와 같은 규칙)
        if (d.isInsurance || (P.settle || []).length) rows.push(['정산서류', P.settle, ['license', 'bank']]);

        return docSec + rows.map((r) => this.photoSec(r[0], r[1], r[2], names)).join('');
    }

    /** 썸네일 1장 — 갤러리에 등록하고 인덱스를 물린다 */
    photoImg(p) {
        const i = this._gallery.length;
        this._gallery.push({ url: p.full });
        return `<img class="ma-photo" src="${this.esc(p.thumb)}" data-idx="${i}" loading="lazy" alt="사진">`;
    }

    /** 사진 한 줄 — 썸네일들 + 그 줄 종류의 ＋ 버튼 */
    photoSec(label, list, kinds, names) {
        // full 이 없는 항목은 전자작업확인서 아이콘(n=0) 뿐이다 — 위 '서류사진' 칸이 실물을 맡는다
        const items = (list || []).filter((p) => p.full).map((p) =>
            `<span class="ma-photo-wrap">${this.photoImg(p)}`
            + (p.tag ? `<em class="ma-photo-tag">${this.esc(p.tag)}</em>` : '')
            + `</span>`).join('');

        const add = kinds.map((k) =>
            `<button type="button" class="ma-photo-add" data-kind="${k}">＋ ${this.esc(names[k] || k)}</button>`).join('');

        return `<div class="ma-sh-sec"><h4>${this.esc(label)}</h4>`
            + (items ? `<div class="ma-photos">${items}</div>` : '')
            + `<div class="ma-photo-adds">${add}</div></div>`;
    }

    /**
     * ＋ 를 누르면 사진을 고른다. 업로드가 끝나면 EventBus('upload:image')로 돌아와 savePhoto 로 간다.
     * (대리진입 중에도 업로드는 열려 있다 — .ai/proxy-login.md §4-4)
     */
    pickPhoto(kind, row) {
        this._upKind = kind;
        this._upRow  = row;
        $j.F.Upload.addPhotoHere({
            // diary1(현장사진) / diary2(작업문서·서류) — 업로드 서버의 파일 구분값
            tag: ['before', 'during', 'after'].includes(kind) ? 'diary1' : 'diary2',
            context: 'maOrderPhoto',
            max_sheet: 1,
            id: row.order_num,
        });
    }

    /**
     * 업로드된 이미지를 오더에 붙인다. 등록 정본은 데스크탑과 같은 Admin\OrderController@addPhoto.
     * 성공하면 상세를 다시 받는다 — 응답의 photos 는 PC 크기(x60)라 그대로 쓰면 썸네일이 작아진다.
     */
    savePhoto(imgId) {
        const row = this._upRow;
        if (!row || !imgId) return;
        $.post(`/ma/ajax/order/${row.order_num}/photos`, { kind: this._upKind, img_id: imgId }, (r) => {
            if (!r || !r.result) return this.toast((r && r.message) || '등록하지 못했습니다');
            this.toast('등록되었습니다');
            this.fetchDetail(row);
        }, 'json');
    }

    /** 이력 탭 — PC 행확장 우측 '작업내용' 로그표 */
    tabLog(d) {
        const logs = d.logs || [];
        if (!logs.length) return '<div class="ma-sh-note">처리이력이 없습니다</div>';
        return `<div class="ma-logs">${logs.map((l) => `
            <div class="ma-log">
                <span class="a">${this.esc(l.action)}${l.desc ? ' · ' + this.esc(l.desc) : ''}</span>
                <span class="t">${this.esc((l.at || '').slice(5, 16))}${l.by ? ' ' + this.esc(l.by) : ''}</span>
            </div>`).join('')}</div>`;
    }

    /** 카드가 쥔 mapRow 로 만드는 스펙 그리드 (상세 도착 전 임시 표시) */
    specRows(row) {
        const PAY = { 1: '싸인', 2: '직수', 5: '카드' };
        const date = (row.work_day || '').slice(5).replace('-', '.');
        const when = [date, row.work_hour_str || row.work_time, row.work_day_type].filter(Boolean).join(' ');
        const kv = [
            ['작업일시', when],
            ['장비', [row.car_type, row.car_range_str].filter(Boolean).join(' ')],
            ['옵션', row.option_str],
            ['수수료', +row.whan_status === 2 ? '환원' : (+row.whan_status === 1 ? '환원보류' : (row.fee_rate != null ? row.fee_rate + '%' : ''))],
            ['결제', PAY[+row.pay_method] || ''],
        ].filter((r) => r[1] != null && r[1] !== '');
        return kv.map((r) => this.kv(r[0], r[1])).join('');
    }

    /** 상세 1회 요청 → 캐시 후 현재 탭 렌더 + 탭 건수 배지 */
    fetchDetail(row) {
        $.getJSON(this.url(row, 'detail')).done((d) => {
            if (!d || d.result === false) return;
            this.detail = d;
            const top = this.sheetEl ? this.sheetEl.scrollTop : 0;  // 저장 후 재조회로 보던 자리를 잃지 않게
            this.renderTab();
            if (this.sheetEl && top) this.sheetEl.scrollTop = top;
            this.tabCounts(d);
        }).fail(() => {
            const box = this.sheetEl && this.sheetEl.querySelector('#maOhBody');
            if (box && !this.detail) box.innerHTML = '<div class="ma-sh-note">상세를 불러오지 못했습니다</div>';
        });
    }

    /** 사진·이력 탭에 건수 배지 */
    tabCounts(d) {
        const P = d.photos || {};   // {doc, work, settle} — 배열이 아니라 줄별 묶음이다
        const photoN = ['doc', 'work', 'settle'].reduce((a, k) => a + (P[k] || []).length, 0);
        const n = { photo: photoN + (d.docPhoto ? 1 : 0), log: (d.logs || []).length };
        (this.sheetEl ? this.sheetEl.querySelectorAll('.ma-tab') : []).forEach((b) => {
            const c = n[b.dataset.tab];
            const $n = b.querySelector('.ma-tab-n');
            if (!$n) return;
            if (c) { $n.textContent = c; $n.hidden = false; } else { $n.hidden = true; }
        });
    }

    kv(k, v) {
        return `<div class="ma-kv"><span class="k">${this.esc(k)}</span><span class="v">${this.esc(v)}</span></div>`;
    }

    /**
     * 편집 가능한 값 한 줄 — 읽기(p) / 편집모드(textarea) 두 모습.
     *
     * 칸마다 모달을 띄우지 않는다. 데스크탑 오더핸들이 셀을 그 자리에서 고치듯 폰에서도
     * **정보 탭 전체를 편집모드로 바꿔** 여러 칸을 이어 고치고 하단 저장바에서 한 번에 보낸다.
     * (칸당 모달은 메모 네 개를 고치는 데 모달을 네 번 열고 닫아야 해서 CS 손이 너무 많이 간다)
     * 저장 경로(POST .../memos)와 허용 컬럼은 데스크탑과 완전히 같다.
     */
    editRow(label, col, val, maxlen) {
        const v = String(val == null ? '' : val);

        // 읽기·편집이 **같은 좌우 배치**다 (제목 왼쪽 │ 내용 오른쪽). 읽을 때 두 줄이었다가
        // 고칠 때 좌우로 바뀌면 같은 값인지 헷갈린다.
        if (!this.editing) {
            const empty = v.trim() === '' ? ' is-empty' : '';
            return `<div class="ma-ed-row${empty}"><span class="k">${this.esc(label)}</span>`
                + `<span class="v"><p>${v.trim() === '' ? '수정을 눌러 입력' : this.esc(v)}</p></span></div>`;
        }
        // 편집칸은 기사 시트(Member.editField)와 **같은 골격**이다 — 라벨 왼쪽 │ 입력 오른쪽,
        // 줄 사이 구분선은 scss(.ma-ed-row)가 긋는다. 두 화면의 편집 모습이 달라 보이면 안 된다.
        // 글자수 카운터는 한도가 있는 칸(작업내용)만. 서버가 자르기 전에 화면에서 먼저 센다.
        const cnt = maxlen ? '<p class="ma-sh-cnt"></p>' : '';
        return `<div class="ma-ed-row"><span class="k">${this.esc(label)}</span><span class="v">`
            + `<textarea class="ma-mdl-in ma-sh-ta" data-col="${col}" data-label="${this.esc(label)}"`
            + ` data-maxlen="${maxlen || 0}" rows="2">${this.esc(v)}</textarea>${cnt}</span></div>`;
    }

    /**
     * 편집모드 토글. 켤 때 지금 값을 손에 쥐어(this.editBase) 저장 때 '바뀐 칸'만 보낸다.
     * 끌 때는 되돌리지 않고 상세를 다시 그린다 — 취소는 입력을 버리는 것이지 저장된 값을 바꾸지 않는다.
     */
    toggleEdit(on) {
        this.editing = !!on;
        this.editBase = on ? Object.assign({}, this.detail?.info) : null;
        this.renderTab();
        if (on) {
            // 켠 블록(오더 정보)이 화면 맨 위에서 시작하게 — 읽기 그리드가 길어 그냥 켜면
            // 입력칸이 접힌 채 열린다. 포커스는 스크롤을 건드리지 않는다(preventScroll).
            // 고치는 칸(작업내용·거래처명·메모)이 탭바 바로 밑에 붙게 — 읽기 그리드는 위로 흘려보낸다
            const box = this.sheetEl && this.sheetEl.querySelector('#maOhBody');
            if (box) scrollSecTop(box.querySelector('[data-sec="memo"]'), this.sheetEl.querySelector('.ma-tabs'));
            // 첫 칸에 자동 포커스하지 않는다 — 키보드가 먼저 올라와 화면을 덮고,
            // CS 가 고르려던 칸이 아닌 곳에 커서가 잡힌다. 고칠 칸은 눌러서 고른다.
        }
    }

    /** 편집모드 textarea 바인딩 — 자동 높이 + 글자수 카운터 */
    bindEditors(box) {
        box.querySelectorAll('.ma-sh-ta').forEach((ta) => {
            const maxlen = +ta.dataset.maxlen || 0;
            const cnt = ta.parentNode.querySelector('.ma-sh-cnt');
            const sync = () => {
                ta.style.height = 'auto';
                ta.style.height = ta.scrollHeight + 'px';   // 줄이 늘면 따라 커진다(스크롤바 없음)
                if (cnt) cnt.innerHTML = `${ta.value.length} / ${maxlen}자 · ${WORK_CONTENT_MAX_LINES}줄까지`;
            };
            ta.addEventListener('input', sync);
            // 키보드가 올라오면 입력칸이 가려진다 — 포커스 시 화면 가운데로 끌어온다
            ta.addEventListener('focus', () => setTimeout(() => ta.scrollIntoView({ block: 'center' }), 250));
            sync();
        });
    }

    /** 편집모드에서 실제로 고친 칸이 있나 (탭 이동·닫기 경고 판정) */
    dirtyEdits() {
        const box = this.sheetEl && this.sheetEl.querySelector('#maOhBody');
        if (!box) return false;
        const base = this.editBase || {};
        return [...box.querySelectorAll('.ma-sh-ta')]
            .some((ta) => (ta.value || '') !== String(base[EDIT_KEY[ta.dataset.col]] ?? ''));
    }

    /**
     * 편집모드 저장 — **바뀐 칸만** 컬럼별로 순차 저장한다.
     * 저장 경로가 칸 하나짜리(POST .../memos, col+memo)라 묶어 보낼 수 없다. 순차인 이유는
     * 같은 행을 동시에 UPDATE 하지 않기 위해서다(검색색인 갱신까지 딸려 있다).
     */
    saveEdits(row) {
        const box = this.sheetEl && this.sheetEl.querySelector('#maOhBody');
        if (!box) return;

        const base = this.editBase || {};
        const jobs = [];
        for (const ta of box.querySelectorAll('.ma-sh-ta')) {
            const col = ta.dataset.col, v = ta.value || '';
            if (v === String(base[EDIT_KEY[col]] ?? '')) continue;   // 안 건드린 칸은 보내지 않는다
            const maxlen = +ta.dataset.maxlen || 0;
            if (maxlen && v.length > maxlen) return this.toast(`${ta.dataset.label}은 ${maxlen}자까지 입력됩니다`);
            jobs.push({ col, memo: v, label: ta.dataset.label });
        }
        if (!jobs.length) { this.toggleEdit(false); return; }

        const url = this.url(row, 'memos');
        const next = (i) => {
            if (i >= jobs.length) {
                this.editing = false;
                this.editBase = null;
                this.toast('저장했습니다');
                // 서버가 작업내용을 줄수·글자수로 자를 수 있어(clampWorkContent) 응답이 아닌
                // 재조회로 맞춘다 — 화면 값과 DB 값이 갈리면 다음 편집이 옛 값에서 시작한다.
                this.fetchDetail(row);
                return;
            }
            $.ajax({ url, type: 'post', data: { col: jobs[i].col, memo: jobs[i].memo } })
                .done((res) => {
                    if (res && res.result === false) return this.toast(`${jobs[i].label} 저장에 실패했습니다`);
                    next(i + 1);
                })
                .fail(() => this.toast('저장에 실패했습니다'));
        };
        next(0);
    }

    /** kv 와 같되 값이 HTML — 뱃지처럼 우리가 만든 마크업만 넣는다(사용자 입력은 kv 로) */
    kvRaw(k, html) {
        return `<div class="ma-kv"><span class="k">${this.esc(k)}</span><span class="v">${html}</span></div>`;
    }

    /** 'YYYY-MM-DD HH:MM:SS' → 'MM.DD HH:MM' (빈 값은 그대로 '') */
    dt(v) {
        const s = String(v || '');
        if (s.length < 16) return s;
        return s.slice(5, 10).replace('-', '.') + ' ' + s.slice(11, 16);
    }

    onAct(el, row, act) {
        switch (act) {
            case 'soojoo':  return this.confirmModal(row, { title: '수주처리', desc: '이 지정대기 오더를 관리자가 대신 수주할까요?', seg: 'soojoo', okLabel: '수주처리' });
            case 'arrive':  return this.confirmModal(row, { title: '현장도착', desc: '현장도착 처리할까요?', seg: 'arrive', okLabel: '현장도착' });
            case 'done':    return this.confirmModal(row, { title: '작업완료', desc: '작업완료 처리할까요?', seg: 'done',     okLabel: '작업완료' });
            case 'payDone': return this.confirmModal(row, { title: '수금완료', desc: '수금완료 처리할까요?', seg: 'pay-done', okLabel: '수금완료' });
            case 'cancel':  return this.cancelModal(row);
            case 'price':   return this.priceModal(row);
            case 'fee':     return this.feeModal(row);
            case 'howtopay': return this.howtopayModal(row);
            case 'hold':    return this.holdModal(row);
            case 'unhold':  return this.unholdModal(row);
            case 'bal-edit': return this.balEdit(row);
            // 아래는 PC 오더핸들 「관리」줄에서 그대로 옮겨온 것들
            case 'rePush':  return this.confirmModal(row, {
                title: '오더재알림', okLabel: '재알림', seg: 're-push',
                desc: '매칭알림을 그대로 한 번 더 보낼까요? 매칭된 전원이 한꺼번에 받고 상태는 그대로입니다.',
            });
            case 'moreBae': return this.moreBaecha(row);
            case 'outBae':  return this.outBaechaModal(row);
            case 'outBaeEdit': return this.outWorkerEditModal(row);
            case 'mercury': return this.mercuryModal(row);
            case 'penaltyCancel': return this.confirmModal(row, {
                title: '페널티취소', okLabel: '페널티취소', okCls: 'danger', seg: 'penalty-cancel',
                desc: '수주자 잘못으로 이 오더를 취소할까요? 오더가 취소되고 수주자 포인트 -200, 수수료는 수주자 10% 부담으로 다시 계산됩니다.',
            });
            case 'penaltyApply': return this.confirmModal(row, {
                title: '페널티적용', okLabel: '페널티적용', okCls: 'danger', seg: 'penalty-apply',
                desc: '취소된 이 오더에 페널티를 적용할까요? 수주자 캐시가 부족하면 처리되지 않습니다.',
            });
            case 'penaltyRevoke': return this.confirmModal(row, {
                title: '페널티해제', okLabel: '페널티해제', seg: 'penalty-revoke',
                desc: '이 오더의 페널티를 해제할까요? 오더는 취소로 남고, 수주자 포인트 -200 이 돌아가며 수수료가 일반 취소(0원)로 다시 계산됩니다.',
            });
            case 'restore': return this.restoreModal(row);
            case 'whanRollback': return this.confirmModal(row, {
                title: '환원원복', okLabel: '환원원복', seg: 'whanwon/rollback',
                desc: '환원처리를 되돌릴까요?',
            });
            case 'whanRelease': return this.confirmModal(row, {
                title: '환원보류해제', okLabel: '해제', seg: 'whanwon/defer-release',
                desc: '환원보류를 해제할까요? 다음 환원배치부터 다시 대상에 포함됩니다.',
            });
            case 'whanDefer': return this.whanDeferModal(row);
            case 'whanRevert': return this.whanRevertModal(row);
            case 'return':    return this.returnModal(row);
        }
    }

    /**
     * 추가배차 — 발주자로 대리진입해 회원 발주화면의 **추가발주 모드**를 연다.
     * PC 는 같은 주소를 아이프레임에 띄우지만($j.F.Admin.Util.userOrder(...,'추가배차')), 폰은 통째로 넘어갔다
     * 복귀 알약으로 돌아온다. 대상은 PC 와 같은 발주자 본인(order_member_num)이다.
     */
    moreBaecha(row) {
        const mbNo = +row.order_member_num || +row.order_owner_num;
        if (!mbNo) return this.toast('발주자를 찾을 수 없습니다');

        const re = `/m/bal?mode=otherorder&editnum=${row.order_num}`;
        location.replace(`/ma/proxy/enter?mb_no=${mbNo}`
            + `&returnurl=${encodeURIComponent(re)}`
            + `&from=${encodeURIComponent(`/ma/orders?open=${row.order_num}`)}`);
    }

    /**
     * 외부배차전환(임의발주) — 회원이 아닌 사람에게 배차한다.
     * 수주여부 판정은 서버(OrderDispatchService::sendOtherByAdmin)가 하고, 반려 사유는
     * act() 가 그대로 토스트로 띄운다. 목록 행이 들고 있는 상태값은 조회 시점 스냅샷이라
     * 여기서 미리 막으면 그 사이에 붙은 수주를 놓친다.
     */
    outBaechaModal(row) {
        const a = (this.detail && this.detail.act) || {};

        this.M.modal({
            title: '외부배차전환', okLabel: '외부배차전환',
            bodyHtml: `<p class="ma-mdl-desc">이 오더를 회원이 아닌 기사에게 배차(임의발주)하고 곧바로 작업완료로 처리합니다.</p>
                <label class="ma-mdl-lb">작업자 이름</label>
                <input type="text" class="ma-mdl-in" id="maMdlOutName" value="${this.esc(a.outerName || '')}">
                <label class="ma-mdl-lb">작업자 전화</label>
                <input type="tel" inputmode="tel" class="ma-mdl-in" id="maMdlOutPhone" value="${this.esc(a.outerPhone || '')}">`,
            onOk: (b, close) => {
                const name  = (b.querySelector('#maMdlOutName').value || '').trim();
                const phone = (b.querySelector('#maMdlOutPhone').value || '').trim();
                if (!name || phone.replace(/[^\d]/g, '').length < 10) {
                    return this.toast('작업자 이름과 전화번호를 입력해주세요');
                }
                this.act(this.url(row, 'sendOther'), 'post',
                    { typech_workerName: name, typech_workerPhone: phone }, close);
            },
        });
    }

    /**
     * 외부배차 수주자 정보변경 — 이미 외부배차(94)된 오더의 이름·전화만 고친다 (PC [외부배차정보변경]과 같은 액션).
     * 초기값은 상세의 act.outerName/outerPhone (외부배차 오더만 채워진다).
     */
    outWorkerEditModal(row) {
        const a = (this.detail && this.detail.act) || {};

        this.M.modal({
            title: '외부배차정보변경', okLabel: '저장',
            bodyHtml: `<p class="ma-mdl-desc">외부배차 수주자의 이름·전화번호를 바꿉니다. 오더 상태는 그대로입니다.</p>
                <label class="ma-mdl-lb">작업자 이름</label>
                <input type="text" class="ma-mdl-in" id="maMdlOutEditName" value="${this.esc(a.outerName || '')}">
                <label class="ma-mdl-lb">작업자 전화</label>
                <input type="tel" inputmode="tel" class="ma-mdl-in" id="maMdlOutEditPhone" value="${this.esc(a.outerPhone || '')}">`,
            onOk: (b, close) => {
                const name  = (b.querySelector('#maMdlOutEditName').value || '').trim();
                const phone = (b.querySelector('#maMdlOutEditPhone').value || '').trim();
                if (!name || phone.replace(/[^\d]/g, '').length < 10) {
                    return this.toast('작업자 이름과 전화번호를 입력해주세요');
                }
                this.act(this.url(row, 'outer-worker'), 'post',
                    { worker_name: name, worker_directphone: phone }, close);
            },
        });
    }

    /** 환원보류 — 이번 환원배치에서 뺀다. 사유는 필수이고 작업내용 로그에 남는다(varchar 50). */
    whanDeferModal(row) {
        this.M.modal({
            title: '환원보류', okLabel: '보류',
            bodyHtml: `<p class="ma-mdl-desc">이번 환원배치(매월 1일) 대상에서 뺍니다.</p>
                <label class="ma-mdl-lb">보류 사유 (필수 · 50자)</label>
                <textarea class="ma-mdl-ta" id="maMdlWhan" maxlength="50"></textarea>`,
            onOk: (b, close) => {
                const memo = (b.querySelector('#maMdlWhan').value || '').trim();
                if (!memo) return this.toast('환원보류 사유를 입력해주세요');
                this.act(this.url(row, 'whanwon/defer'), 'post', { memo }, close);
            },
        });
    }

    /**
     * 환원처리 — 수금완료를 취소하고 곧바로 환원까지 확정한다. 사유는 필수(작업내용 로그).
     * 되돌릴 수 없는 조치라(페널티·포인트차감) 무슨 일이 일어나는지 본문에 그대로 적는다.
     */
    whanRevertModal(row) {
        this.M.modal({
            title: '환원처리', okLabel: '환원처리', okCls: 'danger',
            bodyHtml: `<p class="ma-mdl-desc">수금완료를 취소하고 이 오더를 <b>환원</b>으로 확정합니다.
                발주자에게 <b>환원페널티(발주금지·수주금지)</b>와 <b>포인트 -10,000</b>이 부과되고,
                이미 정산된 <b>수수료는 되돌아갑니다</b> (수주자 환급 · 발주자 회수).</p>
                <label class="ma-mdl-lb">환원 사유 (필수 · 50자)</label>
                <textarea class="ma-mdl-ta" id="maMdlWhanRevert" maxlength="50"></textarea>`,
            onOk: (b, close) => {
                const memo = (b.querySelector('#maMdlWhanRevert').value || '').trim();
                if (!memo) return this.toast('환원 사유를 입력해주세요');
                this.act(this.url(row, 'whanwon/revert'), 'post', { memo }, close);
            },
        });
    }

    /**
     * 회차(작업종료) — 장비가 현장에서 그냥 돌아온 건. 금액을 회차비용으로 갈아끼운다.
     * 금액은 서버가 계산한 값(act.returnPee/returnPeeBonsa)을 그대로 보낸다 — PC 와 같은 인자.
     */
    returnModal(row) {
        const a = (this.detail && this.detail.act) || {};
        this.M.modal({
            title: '회차(작업종료)', okLabel: '회차처리', okCls: 'danger',
            bodyHtml: `<p class="ma-mdl-desc">회차 처리할까요?</p>
                <p class="ma-mdl-help">장비대금이 <b>${this.num(a.price)}원</b> → <b>${this.num(a.returnPee)}원</b> 으로 바뀌고
                작업종료로 넘어갑니다.</p>`,
            onOk: (b, close) => this.act(this.url(row, 'price'), 'post', {
                changeprice: a.returnPee, changeprice_bonsa: a.returnPeeBonsa, changemode: 'workdonereturnset',
            }, close),
        });
    }

    /**
     * 머큐리 RNS 전송현황·재전송 — 보험정산 소속(화물공제·KB) 오더에서만 뜬다.
     * 데스크탑 오더핸들 [머큐리] 모달과 같은 엔드포인트(GET 현황 / POST 재전송)를 쓴다.
     * 읽는 창이라 확인버튼은 없고(hideOk), 전송은 본문 버튼이 직접 한다.
     */
    mercuryModal(row) {
        const m = this.M.modal({
            title: '머큐리 RNS', hideOk: true,
            bodyHtml: '<div class="ma-mc" id="maMc"><div class="ma-mdl-help">불러오는 중…</div></div>',
        });
        const box = m.bodyEl.querySelector('#maMc');

        $.getJSON(this.url(row, 'mercury'))
            .done((d) => { box.innerHTML = this.mercuryHtml(d); this.bindMercury(box, row); })
            .fail(() => { box.innerHTML = '<div class="ma-mdl-help">보험 정산 소속 오더가 아닙니다.</div>'; });
    }

    mercuryHtml(d) {
        const auto = (d.autoTargets || []).join('+') || '오더';
        const err = (d.payload && d.payload._error)
            ? `<div class="ma-mc-note is-fail">전송 불가 : ${this.esc(d.payload._error)}</div>` : '';

        const b = (t, label) => `<button type="button" class="ma-mc-btn" data-mc="${t}">${this.esc(label)}</button>`;

        return `<div class="ma-mc-sum">
                <b>${this.esc(d.insuName) || '보험사 없음'}</b>
                <span>접수번호 ${this.esc(d.receiptNo) || '-'}</span>
                <span>계산서 ${this.esc(d.invoice)}</span>
                <span>${this.esc(d.statusStr)} · ${this.num(d.carPrice)}원</span>
            </div>${err}
            <div class="ma-mc-btns">${b('auto', `자동 (${auto})`)}${b('order', '오더')}${b('estimate', '청구서')}${b('images', '정산서류')}${b('all', '전체')}</div>
            <div id="maMcResult"></div>
            <div class="ma-mc-hd">전송이력<span>벤더로그는 1년 뒤 삭제됩니다 — 비어 있다고 미전송은 아닙니다</span></div>
            <div id="maMcHist">${this.mercuryRows(d.history)}</div>`;
    }

    /** 전송결과·전송이력은 줄 모양이 같다 (성공/실패/개발 뱃지 + 대상 + 메시지) */
    mercuryRows(list) {
        if (!list || !list.length) return '<div class="ma-mdl-help">남아있는 전송이력이 없습니다.</div>';
        return list.map((h) => {
            const ok = h.ok, dev = h.dev;
            const tgt = h.endpoint || h.label || '';
            const msg = h.time ? `${String(h.time).slice(0, 19)} · ${h.message}` : `HTTP ${h.status} · ${h.message}`;
            return `<div class="ma-mc-row ${dev ? 'is-dev' : (ok ? 'is-ok' : 'is-fail')}">`
                + `<span class="ma-mc-badge">${dev ? '개발' : (ok ? '성공' : '실패')}</span>`
                + `<span class="ma-mc-tgt">${this.esc(tgt)}</span>`
                + `<span class="ma-mc-msg">${this.esc(msg)}</span></div>`;
        }).join('');
    }

    bindMercury(box, row) {
        box.querySelectorAll('[data-mc]').forEach((b) => {
            b.onclick = () => {
                const btns = box.querySelectorAll('[data-mc]');
                btns.forEach((x) => { x.disabled = true; });
                const res = box.querySelector('#maMcResult');
                res.innerHTML = '<div class="ma-mdl-help">전송 중…</div>';

                $.post(this.url(row, 'mercury'), { type: b.dataset.mc }, (r) => {
                    btns.forEach((x) => { x.disabled = false; });
                    res.innerHTML = (r.results && r.results.length)
                        ? this.mercuryRows(r.results)
                        : `<div class="ma-mc-note is-fail">${this.esc(r.message || '전송하지 못했습니다.')}</div>`;
                    const hist = box.querySelector('#maMcHist');
                    if (r.history && hist) hist.innerHTML = this.mercuryRows(r.history);
                    this.toast(r.message || (r.result ? '전송했습니다' : '전송 실패'));
                }, 'json').fail(() => {
                    btns.forEach((x) => { x.disabled = false; });
                    res.innerHTML = '<div class="ma-mc-note is-fail">요청에 실패했습니다.</div>';
                });
            };
        });
    }

    /**
     * 홀드 해제 — 다시 아무나 수주할 수 있는 수주대기로 돌린다.
     * 되돌리기가 간단(다시 홀드)하긴 하나 해제 순간 남이 채갈 수 있어 확인을 받는다.
     */
    unholdModal(row) {
        this.M.modal({
            title: '홀드 해제', okLabel: '해제', okCls: 'danger',
            bodyHtml: `<p class="ma-mdl-desc"><b>#${this.esc(row.hold_mb_no)}</b> 에게 걸린 홀드를 풉니다.</p>
                <p class="ma-mdl-help">해제하면 다른 기사도 이 오더를 수주할 수 있고, 작업시간이 지나면 자동취소 대상이 됩니다.
                해제 사실은 그 기사에게 따로 알리지 않습니다.</p>`,
            onOk: (b, close) => this.act(this.url(row, 'hold'), 'delete', {}, close),
        });
    }

    /**
     * 수주대기 홀드 — 회원번호를 받아 그 기사에게 오더를 잡아둔다.
     * 서버는 데스크탑 정본(Admin\OrderController@holdUser) 그대로 — 소속자 차단·수발주제한
     * 검사도 거기서 한다. 해제 기능은 정본에 없어(빈 값이면 서버가 거절) 여기서도 안 만든다.
     */
    holdModal(row) {
        const m = this.M.modal({
            title: row.hold_mb_no ? '홀드 변경' : '홀드 설정', okLabel: '홀드',
            bodyHtml: `<label class="ma-mdl-lb">홀드할 기사</label>
                <input type="text" class="ma-mdl-in" id="maMdlHold" autocomplete="off" enterkeyhint="search"
                       value="${this.esc(row.hold_mb_no || '')}" placeholder="이름·전화·회원번호">
                <div class="ma-ac" id="maMdlHoldAc" hidden></div>
                <p class="ma-mdl-help">이름을 치면 후보가 나옵니다. 고르면 <b>이재일#2831</b> 처럼 채워지고 <b>#뒤 번호</b>가 홀드 대상입니다.
                정상 상태의 소유자만 검색되며, 수·발주 제한이 걸린 기사에게는 홀드되지 않습니다.</p>`,
            onOk: (b, close) => {
                const userNum = this.pickMemberNum(b.querySelector('#maMdlHold').value);
                if (!userNum) return this.toast('목록에서 기사를 고르거나 회원번호를 입력하세요');
                this.act(this.url(row, 'hold'), 'post', { userNum }, close);
            },
        });
        this.bindMemberAc(m.bodyEl.querySelector('#maMdlHold'), m.bodyEl.querySelector('#maMdlHoldAc'));
    }

    /**
     * 검색칸 텍스트 → 회원번호. **보이는 텍스트가 곧 기준이다** (hidden·data-value 금지 규약).
     *   '이재일#2831' → 2831   ·   '2831' → 2831   ·   '이재일' → 0(사람을 특정 못함)
     */
    pickMemberNum(v) {
        const t = String(v == null ? '' : v).trim();
        const m = t.match(/#(\d+)$/);
        if (m) return parseInt(m[1], 10) || 0;
        return /^\d+$/.test(t) ? (parseInt(t, 10) || 0) : 0;
    }

    /**
     * 회원(소유자) 자동완성 — 데스크탑과 같은 정본(Admin\MemberController@findAutoComplete →
     * MemberQueryService::findOwnerAutoComplete). 정상 소유자만 나오므로 홀드 대상 조건과 맞다.
     * 목록은 인풋 아래에 밀어내며 그린다 — 모달 본문이 overflow:auto 라 띄우면 잘린다.
     */
    bindMemberAc($in, $box) {
        if (!$in || !$box) return;
        const draw = (list) => {
            if (!list.length) { $box.hidden = true; $box.innerHTML = ''; return; }
            $box.innerHTML = list.slice(0, 20).map((r) => `
                <button type="button" class="ma-ac-item" data-num="${r.num}" data-name="${this.esc(r.mem_name || '')}">
                    <span class="n">${this.esc(r.mem_name || '-')}<em>#${r.num}</em></span>
                    <span class="p">${this.esc(r.mem_phone || '')}</span>
                </button>`).join('');
            $box.hidden = false;
            $box.querySelectorAll('.ma-ac-item').forEach((b) => {
                b.onclick = () => {
                    $in.value = `${b.dataset.name}#${b.dataset.num}`;
                    $box.hidden = true; $box.innerHTML = '';
                };
            });
        };

        const run = Util.debounce(() => {
            const kwd = ($in.value || '').trim();
            // 이미 고른 상태('이름#번호')거나 한 글자면 굳이 묻지 않는다
            if (kwd.length < 2 || /#\d+$/.test(kwd)) { draw([]); return; }
            $.getJSON('/ma/ajax/members/autocomplete', { kwd })
                .done((list) => draw(Array.isArray(list) ? list : []))
                .fail(() => draw([]));
        }, 250);

        $in.addEventListener('input', run);
    }

    url(row, seg) { return `/ma/ajax/order/${row.order_num}/${seg}`; }

    get M() { return $j.Sites.MobileAdmin; }

    confirmModal(row, { title, desc, seg, okLabel, okCls }) {
        this.M.modal({
            title, okLabel, okCls,
            bodyHtml: `<p class="ma-mdl-desc">${this.esc(desc)}</p>`,
            onOk: (b, close) => this.act(this.url(row, seg), 'post', {}, close),
        });
    }

    cancelModal(row) {
        this.M.modal({
            title: '오더취소', okLabel: '오더취소', okCls: 'danger',
            bodyHtml: `<label class="ma-mdl-lb">취소 사유</label>
                <textarea class="ma-mdl-ta" id="maMdlReason" placeholder="취소 사유"></textarea>
                <label class="ma-mdl-check"><input type="checkbox" id="maMdlExclude"> 발주자 제외(페널티)</label>`,
            onOk: (b, close) => {
                const reason = (b.querySelector('#maMdlReason').value || '').trim();
                if (!reason) return this.toast('취소 사유를 입력해주세요');
                this.act(this.url(row, 'cancel'), 'post', {
                    reason, is_exclude_baljooja: b.querySelector('#maMdlExclude').checked ? 1 : 0,
                }, close);
            },
        });
    }

    /**
     * 금액변경 — /m/ 발주자 모달(F/Baljoo/Modal/Price)을 그대로 열지 않고, 같은 선택값·같은 계산 API
     * (/m/ajax/price, $j.F.Car.calcPrice)를 관리자 자체 모달(.ma-mdl-*, 이미 다른 화면에서 쓰는 CSS라
     * 깨질 일이 없다) 위에 새로 그린다. 본사가 수동수정(edit_bonsa)도 그대로 유지한다.
     */
    priceModal(row) {
        $.getJSON(this.url(row, 'price/form')).done((f) => {
            if (!f || f.result === false) return this.toast(f && f.message ? f.message : '없는 오더입니다');

            const bonsa = f.edit_bonsa
                ? `<label class="ma-mdl-lb">본사가</label><input type="text" inputmode="numeric" class="ma-mdl-in num" id="maMdlPriceB" data-amount-kor value="${this.num(f.car_price_bonsa)}">`
                : '';

            // 시간유형 — /m/ 발주자 모달과 같은 값·같은 장비별 숨김(F/Baljoo/Modal/Price drawLeft)
            let typeList = ['월', '일', '오전', '오후', '시간', '야간', '톤'];
            if (f.car_type === '리프트') typeList = typeList.filter((t) => !['오전', '오후', '시간', '야간'].includes(t));
            if (f.car_type !== '사다리') typeList = typeList.filter((t) => t !== '톤');
            const typeOpts = typeList
                .map((t) => `<option value="${t}"${t === f.car_workdaytype ? ' selected' : ''}>${t}</option>`)
                .join('');

            const { bodyEl } = this.M.modal({
                title: '금액변경', okLabel: '변경',
                bodyHtml: `<label class="ma-mdl-lb">장비대금</label>
                    <input type="text" inputmode="numeric" class="ma-mdl-in num" id="maMdlPrice" data-amount-kor value="${this.num(f.car_price)}">${bonsa}
                    <hr class="ma-mdl-divider">
                    <label class="ma-mdl-lb">작업소요시간</label>
                    <select class="ma-mdl-in" id="maMdlWorkType">${typeOpts}</select>
                    <select class="ma-mdl-in" id="maMdlWorkHour" style="margin-top:8px"></select>
                    <label class="ma-mdl-lb">추가작업시간</label>
                    <div style="display:flex;gap:8px">
                        <select class="ma-mdl-in" id="maMdlAddType" style="width:auto;flex:1;min-width:0">
                            <option value="0">없음</option><option value="1">주간</option><option value="2">야간</option>
                        </select>
                        <select class="ma-mdl-in" id="maMdlAddHour" style="width:auto;flex:1;min-width:0" disabled></select>
                    </div>
                    <p class="ma-mdl-help" id="maMdlCalcHelp" style="min-height:18px"></p>`,
                onOk: (b, close) => {
                    const type = b.querySelector('#maMdlWorkType').value;
                    const isHalfDay = type === '오전' || type === '오후';
                    const addType = b.querySelector('#maMdlAddType').value;
                    const data = {
                        changeprice: (b.querySelector('#maMdlPrice').value || '').replace(/[^\d]/g, ''),
                        changemode: 'priceset',
                        jobgoobun: type,
                        jobworkhr: isHalfDay ? 4 : parseFloat(b.querySelector('#maMdlWorkHour').value || '0'),
                        addHourType: addType === '0' ? '' : addType,
                        addHourIntval: addType === '0' ? 0 : parseInt(b.querySelector('#maMdlAddHour').value || '0', 10),
                        addHourIntprice: addHourPrice,
                    };
                    if (f.edit_bonsa) data.changeprice_bonsa = (b.querySelector('#maMdlPriceB').value || '').replace(/[^\d]/g, '');

                    // 스카이 저단가 하한 아래로 내리면 발주자의 -6차·발주개수가 끊긴다 (2026-09-01~).
                    const price = parseInt(data.changeprice || '0', 10);
                    const send = () => this.act(this.url(row, 'price'), 'post', data, close);
                    if (!skyLowLimit || price >= skyLowLimit) return send();

                    $j.Ui.Dialog.open({
                        tone: 'danger',
                        title: '저단가로 변경됩니다',
                        desc: '이 금액으로 바꾸면 발주자에게',
                        emph: '-6차 · 발주개수가 지급되지 않습니다',
                        rows: [
                            { k: '변경 금액', v: `${price.toLocaleString()}원`, tone: 'danger' },
                            { k: '저단가 하한', v: `${skyLowLimit.toLocaleString()}원` },
                        ],
                        confirm: '변경',
                        cancel: '취소',
                        onConfirm: send,
                    });
                },
            });

            const $b = $(bodyEl);
            const $type = $b.find('#maMdlWorkType');
            const $hour = $b.find('#maMdlWorkHour');
            const $addType = $b.find('#maMdlAddType');
            const $addHour = $b.find('#maMdlAddHour');
            const $price = $b.find('#maMdlPrice');
            const $priceB = $b.find('#maMdlPriceB');
            const $help = $b.find('#maMdlCalcHelp');

            // /adm 금액변경(OrderHandle/orderEdit.js price_form)과 같은 게이트다 — 최초값은 조회 응답, 재계산할 때마다 갱신
            let skyLowLimit = parseInt(f.sky_low_limit || '0', 10);
            let addHourPrice = parseInt(f.add_hour_price || '0', 10);   // 미조정시 현재값 그대로 재저장(idempotent)

            const hourOptionsFor = (type) => {
                const hl = (n) => $j.F.Car.hourLabel(n, f.car_type);
                switch (type) {
                    case '월': return Array.from({ length: 12 }, (_, i) => i + 1).map((n) => [n, `${n}개월`]);
                    case '일': return Array.from({ length: f.car_type === '리프트' ? 30 : 59 }, (_, i) => f.car_type === '리프트' ? i + 1 : 1 + i / 2).map((n) => [n, `${n}일`]);   // 1.5일 — 리프트는 반나절 단가가 없다
                    case '시간': return Array.from({ length: 12 }, (_, i) => i + 1).map((n) => [n, hl(n)]);
                    case '야간': return Array.from({ length: 12 }, (_, i) => i + 1).map((n) => [n, `야간 ${hl(n)}`]);
                    case '톤': return [1, 2, 5, 6, 7.5, 10, 12.5, 15, 17.5, 20].map((v) => [v, `${v}톤`]);
                    default: return []; // 오전/오후 — 상세시간 없이 반나절(4시간) 고정
                }
            };
            // 오전/오후는 상세시간이 없다 — 그래도 칸을 지우지 않고 비활성 선택지 하나만 채운다.
            // 숨기면(hide) 그 줄만큼 모달 높이가 바뀌어(다른 유형과 다르게) 화면이 들썩인다.
            const rebuildHourSelect = () => {
                const type = $type.val();
                if (type === '오전' || type === '오후') {
                    $hour.prop('disabled', true).html('<option value="4">반나절(4시간 고정)</option>');
                    return;
                }
                $hour.prop('disabled', false);
                const cur = parseFloat(f.car_workhour);
                $hour.html(hourOptionsFor(type)
                    .map(([v, label]) => `<option value="${v}"${cur === v ? ' selected' : ''}>${label}</option>`)
                    .join(''));
            };
            const rebuildAddHourList = () => {
                if ($addHour.children().length) return;
                $addHour.html(Array.from({ length: 13 }, (_, i) => i)
                    .map((n) => `<option value="${n}">${n === 0 ? '없음' : $j.F.Car.hourLabel(n, f.car_type)}</option>`)
                    .join(''));
            };
            rebuildHourSelect();
            rebuildAddHourList();
            if (parseInt(f.add_hour_type, 10)) {
                $addType.val(String(f.add_hour_type));
                $addHour.val(String(f.add_hour || 0));
            }
            $addHour.prop('disabled', $addType.val() === '0');

            const recalc = () => {
                const type = $type.val();
                const isHalfDay = type === '오전' || type === '오후';
                const addOn = $addType.val() !== '0';
                $j.F.Car.calcPrice({
                    car_type: f.car_type, car_range: f.car_range, car_option: f.car_option,
                    sosok: f.order_sosok,
                    car_workdaytype: type,
                    car_workhour: isHalfDay ? 4 : ($hour.val() || 0),
                    is_retun_job: 0,
                    car_workaddtype: addOn ? $addType.val() : '',
                    car_workaddhor: addOn ? ($addHour.val() || 0) : 0,
                    work_start_h: String(f.work_time || '').substr(11, 2), work_day: f.work_day, ordernum: f.num,
                    order_type: f.order_type,// 저단가 하한은 어플발주만 — 아니면 서버가 0 을 준다
                }, (out) => {
                    if (!out) return;
                    const price = (parseInt(out.base_price, 10) || 0) + (parseInt(out.more_price, 10) || 0);
                    $price.val(this.num(price));
                    if (f.edit_bonsa) $priceB.val(this.num(out.bonsa_price || 0));
                    addHourPrice = parseInt(out.more_price, 10) || 0;
                    skyLowLimit = parseInt(out.sky_low_limit || '0', 10);
                    $help.text(skyLowLimit && price < skyLowLimit ? `스카이 저단가 — 하한 ${skyLowLimit.toLocaleString()}원 미만입니다` : '');
                });
            };
            $type.on('change', () => { rebuildHourSelect(); recalc(); });
            $hour.on('change', recalc);
            $addType.on('change', () => { $addHour.prop('disabled', $addType.val() === '0'); recalc(); });
            $addHour.on('change', recalc);
        }).fail(() => this.toast('요청 실패'));
    }

    /**
     * 취소원복 — 취소 순간의 스냅샷으로 되돌린다. PC 오더핸들 restore_cancel() 과 같은 엔드포인트·같은 계획.
     * 무엇을 되돌릴지는 서버가 계산한 계획을 그대로 보여준다 (화면이 따로 셈하지 않는다).
     */
    restoreModal(row) {
        $.getJSON(this.url(row, 'restore/plan')).done((p) => {
            if (!p || !p.result) return this.toast(p && p.message ? p.message : '되돌릴 수 없는 오더입니다');
            const rows  = (p.rows || []).map((r) => `<p class="ma-mdl-help"><b>${this.esc(r.k)}</b> ${this.esc(r.v)}</p>`).join('');
            const lines = (p.plan || []).map((l) => `<p class="ma-mdl-help">· ${this.esc(l)}</p>`).join('');
            const warns = (p.warnings || []).map((w) => `<p class="ma-mdl-help">⚠ ${this.esc(w)}</p>`).join('');
            this.M.modal({
                title: '취소원복', okLabel: '되돌리기', okCls: 'danger',
                bodyHtml: `<p class="ma-mdl-desc">취소된 오더를 취소 직전 상태 <b>[${this.esc(p.targetLabel)}]</b> 로 되살립니다.
                    수주자·발주자의 캐시·포인트·발주개수·-6차가 함께 움직입니다. 실수로 취소한 경우에만 쓰세요.</p>
                    <label class="ma-mdl-lb">스냅샷 (취소 직전)</label>${rows}
                    <label class="ma-mdl-lb">되돌릴 항목</label>${lines}${warns}
                    <input class="ma-mdl-in" id="maMdlRestoreMemo" type="text" maxlength="60" placeholder="메모 (선택 · 오더로그에 남음)">
                    <label class="ma-mdl-check"><input type="checkbox" id="maMdlRestoreAgree"> 위 내용을 확인했습니다</label>`,
                onOk: (b, close) => {
                    if (!b.querySelector('#maMdlRestoreAgree').checked) return this.toast('내용을 확인하고 체크해 주세요');
                    this.act(this.url(row, 'restore'), 'post', {
                        memo: (b.querySelector('#maMdlRestoreMemo').value || '').trim(),
                    }, close);
                },
            });
        }).fail(() => this.toast('요청 실패'));
    }

    feeModal(row) {
        $.getJSON(this.url(row, 'fee/form')).done((f) => {
            if (!f || f.result === false) return this.toast(f && f.message ? f.message : '없는 오더입니다');
            this.M.modal({
                title: '수수료 변경', okLabel: '변경',
                bodyHtml: `<p class="ma-mdl-desc">${f.msg || '수수료를 변경할까요?'}</p>`,
                onOk: (b, close) => this.act(this.url(row, 'fee'), 'post', { is_fee: f.is_fee_target }, close),
            });
        }).fail(() => this.toast('요청 실패'));
    }

    /**
     * 결제책임 변경 — 싸인·직수·카드 중 고르고, 싸인이면 결제날짜까지.
     * 선택지는 발주화면 결제책임 모달(F/Baljoo/Modal/HowToPay)과 같은 상수(G5_HOWTOPAY·G5_SIGN_HOWLOG)다.
     * 서버(OrderModificationService::changeHowtopayByAdmin)가 잔액을 보고 막거나, 작업완료(8)면 캐시를 다시 계산한다.
     */
    howtopayModal(row) {
        const i = (this.detail && this.detail.info) || {};
        const PAY_MAP = G5['HOW_TO_PAY'] || {}, DUE = G5['SIGN_DUE'] || {};
        const cur = +i.howtopayVal || 0;
        const seg = Object.keys(PAY_MAP).filter((k) => +k !== 0)
            .map((k) => `<button type="button" class="ma-mdl-seg-btn${+k === cur ? ' is-on' : ''}" data-v="${k}">${this.esc(PAY_MAP[k])}</button>`)
            .join('');
        const due = Object.keys(DUE).filter((k) => +k !== 0)
            .map((k) => `<option value="${k}"${+k === +i.signHowlong ? ' selected' : ''}>${this.esc(DUE[k])}</option>`).join('');

        const { bodyEl } = this.M.modal({
            title: '결제책임 변경', okLabel: '변경',
            bodyHtml: `<div class="ma-mdl-pay"><p class="ma-mdl-help">지금: <b>${this.esc(i.howtopay || '미선택')}</b></p>
                <label class="ma-mdl-lb">결제방식</label>
                <div class="ma-mdl-seg">${seg}</div>
                <div id="maMdlSignWrap"${cur === 1 ? '' : ' hidden'}>
                    <label class="ma-mdl-lb">싸인 결제날짜</label>
                    <select class="ma-mdl-in" id="maMdlSign"><option value="0">결제날짜 선택</option>${due}</select>
                </div>
                ${+row.order_status === 8 ? '<p class="ma-mdl-help">작업완료 단계라 수수료·캐시가 다시 계산됩니다.</p>' : ''}</div>`,
            onOk: (b, close) => {
                const on = b.querySelector('.ma-mdl-seg-btn.is-on');
                const howtopay = on ? +on.dataset.v : 0;
                if (!howtopay) return this.toast('결제방식을 선택해주세요');
                const sign = howtopay === 1 ? +b.querySelector('#maMdlSign').value : 0;
                if (howtopay === 1 && !sign) return this.toast('싸인 결제날짜를 선택해주세요');
                if (howtopay === cur && (howtopay !== 1 || sign === +i.signHowlong)) { close(); return; }
                // 시트를 닫지 않고 관리 탭에 그대로 둔다 — 상세만 다시 받아 버튼·정보탭 값을 맞춘다
                this.act(this.url(row, 'howtopay'), 'post', { howtopay, sign_howlong_int: sign }, close, () => {
                    row.pay_method = howtopay;
                    this.fetchDetail(row);
                });
            },
        });
        bodyEl.querySelectorAll('.ma-mdl-seg-btn').forEach((btn) => {
            btn.onclick = () => {
                bodyEl.querySelectorAll('.ma-mdl-seg-btn').forEach((x) => x.classList.toggle('is-on', x === btn));
                bodyEl.querySelector('#maMdlSignWrap').hidden = btn.dataset.v !== '1';
            };
        });
    }

    /**
     * 액션 요청 공통 처리 — 성공 시 모달·시트 닫고 목록 갱신.
     * onDone 을 넘기면 모달만 닫고 시트는 그대로 둔 채 onDone 을 부른다(목록 갱신 없음).
     */
    act(url, method, data, closeModal, onDone) {
        $.ajax({ url, type: method, data })
            .done((res) => {
                const obj = (res && typeof res === 'object') ? res : {};
                // 서버가 한 번 확인을 요구한다(카드결제 결과 확인 중인 오더의 수금완료 — CardOrderService::pendingNotice).
                // 확인하면 같은 요청에 card_pending_ok 를 실어 다시 보낸다
                if (obj.confirm) {
                    Util.swalert(obj.message, () => this.act(url, method, Object.assign({}, data, { card_pending_ok: 1 }), closeModal, onDone), ['계속', '취소']);
                    return;
                }
                // result 를 아예 안 내는 액션(재알림·환원원복)은 200 이면 성공이다.
                // 내는 쪽은 true/1 만 성공 — 페널티 액션은 실패를 0 으로도 준다(1 미만은 전부 실패).
                const has = Object.prototype.hasOwnProperty.call(obj, 'result');
                const fail = has && (obj.result === false || (typeof obj.result === 'number' && obj.result < 1));
                const msg = obj.message || obj.msg || (fail ? '처리하지 못했습니다' : '처리되었습니다');
                this.toast(msg);
                if (fail) return;
                closeModal && closeModal();
                if (onDone) return onDone();
                $j.Ui.BS.close();
                this.reload();
            })
            .fail((xhr) => this.toast(xhr.status === 401 ? '세션이 만료되었습니다' : '요청에 실패했습니다'));
    }

    toast(msg) { if ($j.Ui && $j.Ui.Toast) $j.Ui.Toast.show(msg); }

    num(v) { return (parseInt(String(v).replace(/[^\d-]/g, ''), 10) || 0).toLocaleString('ko-KR'); }

    esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
